/**
 * CCC Interactive Flavour Wheel — partial sunburst flavour explorer.
 * Reads the taxonomy asset (coffee-flavour-wheel-data.json) for category
 * order/colours and counts live products from the collection grid, so the
 * wheel always matches what is actually in the shop. Selections are applied
 * to the on-page grid via the `coffee:wheel-apply` event handled by
 * coffee-collection.js.
 */

const SVG_NS = 'http://www.w3.org/2000/svg';
const SIZE = 680;
const CX = SIZE / 2;
const CY = SIZE / 2;
const R_INNER_0 = 92;
const R_INNER_1 = 166;
const R_OUTER_0 = 170;
const R_OUTER_1 = 316;
const ARC_TOTAL = 360; // full circle
const ARC_START = 0; // first family starts at 12 o'clock, clockwise
const MIN_DESCRIPTOR_ANGLE = 3;
const MIN_FAMILY_ANGLE = 8; // no family renders narrower than this (≈ Tea)
const LABEL_LG_ANGLE = 5.4; // full-size label
const LABEL_SM_ANGLE = 3.02; // compact label — effectively every segment
const CATEGORY_FONT = 13;
// Uppercase tracked text ≈ 0.74em per character; used to fit labels to arcs.
const CATEGORY_CHAR_EM = 0.74;
const CATEGORY_SHORT_NAMES = {
  'Herbal & Spice': 'Herbal',
  'Floral & Tea': 'Floral',
  'Orchard Fruit': 'Orchard',
  'Winey & Boozy': 'Winey',
  'Stone Fruit': 'Stone',
  'Dried Fruit': 'Dried',
  'Chocolate': 'Choc',
};

let built = false;
let hasAnimatedIn = false;
let taxonomy = null;

/**
 * LITE MODE — slow devices get a fast, plain wheel: no sound, no fade, no
 * glows, static tutorial. Decided from the measured CPU cost of building the
 * wheel (a real benchmark of this device running this exact workload) plus a
 * low-memory hint; the camera zoom stays (compositor-only, cheap everywhere).
 */
let liteMode = false;
let buildCpuMs = 0; // accumulated by the build steps
const LITE_BUILD_MS = 900; // ≈3× a desktop build; only genuinely slow devices exceed it

function decideLiteMode() {
  if (!liteMode) {
    liteMode =
      buildCpuMs > LITE_BUILD_MS ||
      (navigator.deviceMemory !== undefined && navigator.deviceMemory <= 3);
  }
  if (liteMode) {
    const modal = getModal();
    if (modal) modal.classList.add('coffee-wheel-modal--lite');
  }
}

/** 'all' | 'espresso' | 'filter' — narrows live match counts + the applied filter. */
let roastMode = 'all';

/**
 * Locked intro/settle tuning values, read by applyTune() and playIntro().
 */
const TUNE = {
  easeIn: 0.42, // settle bezier x1 (0 = none, 1 = strong ease-in)
  easeOut: 0.7, // settle bezier: higher = softer landing
  settleDur: 650, // ms
  glow: 12, // px, selected-slice glow radius
  vivid: 1.0, // saturation multiplier on the whole wheel
  waveStep: 14, // ms between slices in the intro wave
  waveDur: 420, // ms each slice takes to grow
};

function applyTune() {
  const modal = getModal();
  if (!modal) return;
  modal.style.setProperty('--wheel-settle-ease', `cubic-bezier(${TUNE.easeIn}, 0, ${(1 - TUNE.easeOut).toFixed(2)}, 1)`);
  modal.style.setProperty('--wheel-settle-dur', `${TUNE.settleDur}ms`);
  modal.style.setProperty('--wheel-glow', `${TUNE.glow}px`);
  modal.style.setProperty('--wheel-vivid', String(TUNE.vivid));
  modal.style.setProperty('--wheel-wave-step', `${TUNE.waveStep}ms`);
  modal.style.setProperty('--wheel-wave-dur', `${TUNE.waveDur}ms`);
}

// ---- audio: subtle synth feedback (created lazily on user gesture) ----
let audioCtx = null;
let masterBus = null;

function audio() {
  if (liteMode) return null; // slow device: every voice becomes a no-op
  if (!audioCtx) {
    const Ctx = window.AudioContext || window.webkitAudioContext;
    if (!Ctx) return null;
    audioCtx = new Ctx();
  }
  if (audioCtx.state === 'suspended') audioCtx.resume();
  return audioCtx;
}

/**
 * Shared output bus: a compressor + gain so overlapping trills (fast thumb
 * rolls, the intro ripple) never stack into clipping. Every voice routes here
 * instead of straight to the destination.
 */
function master() {
  const ctx = audio();
  if (!ctx) return null;
  if (!masterBus) {
    const comp = ctx.createDynamicsCompressor();
    comp.threshold.value = -20;
    comp.knee.value = 26;
    comp.ratio.value = 12;
    comp.attack.value = 0.003;
    comp.release.value = 0.2;
    const g = ctx.createGain();
    g.gain.value = 0.85;
    comp.connect(g).connect(ctx.destination);
    masterBus = comp;
  }
  return masterBus;
}

/**
 * Warm melodic tone — flavour highlight feedback. A low sine fundamental
 * with a quiet fifth above it, softly low-passed, gliding gently upward.
 */
function trill(freq = 440, gain = 0.05, dur = 0.26) {
  const ctx = audio();
  if (!ctx) return;
  const t = ctx.currentTime;

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(2400, t);
  lp.frequency.exponentialRampToValueAtTime(900, t + dur);
  lp.Q.value = 0.6;

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, t);
  amp.gain.exponentialRampToValueAtTime(gain, t + 0.025);
  amp.gain.exponentialRampToValueAtTime(0.0001, t + dur);

  const fundamental = ctx.createOscillator();
  fundamental.type = 'sine';
  fundamental.frequency.setValueAtTime(freq, t);
  fundamental.frequency.exponentialRampToValueAtTime(freq * 1.04, t + dur);

  const fifth = ctx.createOscillator();
  fifth.type = 'sine';
  fifth.frequency.setValueAtTime(freq * 1.5, t);
  fifth.frequency.exponentialRampToValueAtTime(freq * 1.5 * 1.04, t + dur);
  const fifthGain = ctx.createGain();
  fifthGain.gain.value = 0.3;

  fundamental.connect(amp);
  fifth.connect(fifthGain).connect(amp);
  amp.connect(lp).connect(master());
  fundamental.start(t);
  fifth.start(t);
  fundamental.stop(t + dur + 0.03);
  fifth.stop(t + dur + 0.03);
}

/**
 * Warm "ooo" confirmations — richer and more establishing than a hover trill,
 * played when a flavour is added. Root + fifth + soft octave rise under a
 * gentle low-pass, so it reads as a pleased "ooh", not an alert.
 *
 * Cycles through three cohesive variants so repeated picks don't fatigue: the
 * roots sit on a warm pentatonic (D–E–G), so successive selections chime like
 * a little melody, with small timbre shifts (octave level, filter peak, glide).
 */
const SELECT_CHIME_VARIANTS = [
  { root: 294, oct: 0.18, lpPeak: 1500, glide: 1.122 }, // D4
  { root: 330, oct: 0.14, lpPeak: 1620, glide: 1.122 }, // E4
  { root: 392, oct: 0.2, lpPeak: 1440, glide: 1.091 }, // G4
];
let selectChimeIndex = 0;

function selectChime() {
  const ctx = audio();
  if (!ctx) return;
  const v = SELECT_CHIME_VARIANTS[selectChimeIndex % SELECT_CHIME_VARIANTS.length];
  selectChimeIndex += 1;
  const t = ctx.currentTime;

  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(650, t);
  lp.frequency.exponentialRampToValueAtTime(v.lpPeak, t + 0.16);
  lp.frequency.exponentialRampToValueAtTime(620, t + 0.5);
  lp.Q.value = 0.9;

  const amp = ctx.createGain();
  amp.gain.setValueAtTime(0.0001, t);
  amp.gain.exponentialRampToValueAtTime(0.05, t + 0.045);
  amp.gain.setValueAtTime(0.05, t + 0.17);
  amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.52);

  const partials = [
    { mult: 1, gain: 1, type: 'sine' },
    { mult: 1.5, gain: 0.42, type: 'sine' },
    { mult: 2, gain: v.oct, type: 'triangle' },
  ];
  partials.forEach((p) => {
    const osc = ctx.createOscillator();
    osc.type = p.type;
    osc.frequency.setValueAtTime(v.root * p.mult, t);
    osc.frequency.exponentialRampToValueAtTime(v.root * p.mult * v.glide, t + 0.26);
    const g = ctx.createGain();
    g.gain.value = p.gain;
    osc.connect(g).connect(amp);
    osc.start(t);
    osc.stop(t + 0.55);
  });
  amp.connect(lp).connect(master());
}

/** Synthetic "doooosh" — the settle impact. */
function doosh() {
  const ctx = audio();
  if (!ctx) return;
  const t = ctx.currentTime;
  const osc = ctx.createOscillator();
  const amp = ctx.createGain();
  osc.type = 'sine';
  osc.frequency.setValueAtTime(170, t);
  osc.frequency.exponentialRampToValueAtTime(48, t + 0.55);
  amp.gain.setValueAtTime(0.0001, t);
  amp.gain.exponentialRampToValueAtTime(0.22, t + 0.03);
  amp.gain.exponentialRampToValueAtTime(0.0001, t + 0.7);
  osc.connect(amp).connect(master());
  osc.start(t);
  osc.stop(t + 0.75);

  // airy body: filtered noise burst
  const frames = Math.floor(ctx.sampleRate * 0.4);
  const buffer = ctx.createBuffer(1, frames, ctx.sampleRate);
  const data = buffer.getChannelData(0);
  for (let i = 0; i < frames; i++) data[i] = (Math.random() * 2 - 1) * (1 - i / frames);
  const noise = ctx.createBufferSource();
  noise.buffer = buffer;
  const lp = ctx.createBiquadFilter();
  lp.type = 'lowpass';
  lp.frequency.setValueAtTime(420, t);
  lp.frequency.exponentialRampToValueAtTime(120, t + 0.4);
  const nAmp = ctx.createGain();
  nAmp.gain.setValueAtTime(0.12, t);
  nAmp.gain.exponentialRampToValueAtTime(0.0001, t + 0.42);
  noise.connect(lp).connect(nAmp).connect(master());
  noise.start(t);
}

const PENTATONIC = [0, 3, 5, 7, 10];

function pentatonicFreq(index) {
  const step = PENTATONIC[index % PENTATONIC.length] + 12 * Math.floor((index % 15) / 5);
  return 330 * Math.pow(2, step / 12);
}
/** @type {Set<string>} */
const selected = new Set();
/** @type {Map<string, { label: string, count: number, category: string }>} */
let descriptorInfo = new Map();
/** @type {Map<string, { hue: number, sat: number, light: number }>} family slug -> colour */
let familyColors = new Map();
/** @type {Map<string, Set<string>>} slug -> product ids */
let productsBySlug = new Map();
/** @type {Map<string, string>} product id -> short display title */
let productTitles = new Map();
/** @type {Map<string, { roast: string, price: string }>} product id -> row metadata */
let productMeta = new Map();
/** @type {Map<string, { slug: string, label: string }[]>} product id -> its flavour notes */
let productDescriptors = new Map();
let inStockProductCount = 0;

/** Angular geometry for touch drag hit-testing (filled during buildWheel). */
/** @type {{ a0: number, a1: number, slug: string, name: string, count: number, color: object }[]} */
let outerSegments = [];
/** @type {{ a0: number, a1: number, slug: string, slugs: string[], name: string, count: number }[]} */
let categorySegments = [];
/** slug -> path element, so the drag loop never has to querySelector mid-move. */
let descriptorEls = new Map();
let categoryEls = new Map();
/** slug -> radial label element (when one was rendered), for hover emphasis. */
let descriptorLabelEls = new Map();
/**
 * Labels whose reading orientation must track the dial rotation. Radial
 * labels flip at the 180° seam (the left half reads inward), arc labels
 * reverse on the bottom half (90°–270°) — both baked at build time for the
 * REST angle, so labels the dial scrolls to the far side render upside down
 * unless re-flipped live against their CURRENT on-screen angle.
 */
/** @type {{ el: SVGTextElement, mid: number, flipped: boolean }[]} */
let radialFlipLabels = [];
/** @type {{ pathEl: SVGPathElement, a0: number, a1: number, rText: number, reversed: boolean }[]} */
let arcFlipLabels = [];

/**
 * Re-flip any label whose current angle (rest mid + rotation) crossed a seam.
 * `onlyNearWindow` limits the writes to labels within ~±70° of the reading
 * window: at 3× that's everything visible, and each skipped write avoids a
 * raster invalidation inside a heavily-scaled layer (a full-wheel pass per
 * degree measurably dropped scroll fps on throttled devices). A full pass
 * runs whenever the rotation settles, so nothing stays stale at rest.
 */
function applyRadialFlip(entry, rot) {
  const a = (((entry.mid + rot) % 360) + 360) % 360;
  const flipped = a > 180;
  if (flipped === entry.flipped) return;
  entry.flipped = flipped;
  // x/y read live — the collision sweep may have slid this label's spoke
  const x = parseFloat(entry.el.getAttribute('x'));
  const y = parseFloat(entry.el.getAttribute('y'));
  const rotation = flipped ? entry.mid + 90 : entry.mid - 90;
  entry.el.setAttribute(
    'transform',
    `rotate(${rotation.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)})`
  );
}

function applyArcFlip(entry, rot) {
  const mid = (entry.a0 + entry.a1) / 2;
  const a = (((mid + rot) % 360) + 360) % 360;
  const reversed = a > 90 && a < 270;
  if (reversed === entry.reversed) return;
  entry.reversed = reversed;
  // redraw the textPath arc in the opposite direction (and swap the side
  // offset) so the curved family name reads upright at its current angle
  const r = reversed ? entry.rText - 4.5 : entry.rText + 4.5;
  const [sx, sy] = polar(r, reversed ? entry.a1 : entry.a0);
  const [ex, ey] = polar(r, reversed ? entry.a0 : entry.a1);
  const large = entry.a1 - entry.a0 > 180 ? 1 : 0;
  entry.pathEl.setAttribute(
    'd',
    `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 ${large} ${reversed ? 0 : 1} ${ex.toFixed(2)} ${ey.toFixed(2)}`
  );
}

/**
 * Re-flip labels whose current angle (rest mid + rotation) crossed a reading
 * seam — but ONLY those within ±70° of the reading window: at 3× that covers
 * everything visible, and every skipped write avoids a raster invalidation
 * inside a heavily-scaled layer (a full-wheel pass per degree measurably
 * dropped scroll fps on throttled phones).
 */
function updateLabelFlips(rot) {
  const farFromWindow = (a) => Math.abs(((a - WINDOW_DEG + 540) % 360) - 180) > 70;
  radialFlipLabels.forEach((entry) => {
    const a = (((entry.mid + rot) % 360) + 360) % 360;
    if (!farFromWindow(a)) applyRadialFlip(entry, rot);
  });
  arcFlipLabels.forEach((entry) => {
    const a = ((((entry.a0 + entry.a1) / 2 + rot) % 360) + 360) % 360;
    if (!farFromWindow(a)) applyArcFlip(entry, rot);
  });
}

/**
 * Restore every label to its rest orientation in small idle batches — one
 * big write burst at gesture end showed up as a ~340ms task on throttled
 * phones. A new gesture supersedes any restore still in flight.
 */
let flipRestoreToken = 0;

function cancelLabelFlipRestore() {
  flipRestoreToken += 1;
}

function restoreLabelFlipsChunked() {
  const token = ++flipRestoreToken;
  const radial = radialFlipLabels;
  const arcs = arcFlipLabels;
  let i = 0;
  const total = radial.length + arcs.length;
  const step = () => {
    if (token !== flipRestoreToken) return; // superseded by a new gesture
    const end = Math.min(i + 16, total);
    for (; i < end; i++) {
      if (i < radial.length) applyRadialFlip(radial[i], 0);
      else applyArcFlip(arcs[i - radial.length], 0);
    }
    if (i < total) idleSlice(step);
  };
  step();
}

/** True on phones/small tablets — used to skip desktop-only build work and to
 *  gate the mobile-only micro-zoom + light intro. Cached; the wheel is built
 *  once per page load so a live breakpoint flip is a non-issue in practice. */
function isMobileViewport() {
  return window.matchMedia('(max-width: 749px)').matches;
}

function prefersReducedMotion() {
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** "Colombia | Las Flores | Jhoan Vergara | Chiroso…" -> "Colombia — Las Flores" */
function shortTitle(title) {
  const parts = String(title)
    .split('|')
    .map((p) => p.trim())
    .filter(Boolean);
  return parts.slice(0, 2).join(' — ');
}

/* memoised — getModal is on the drag hot path (via coffeesLabel) and a
   whole-document query per slice change showed up in profiles */
let modalEl = null;
function getModal() {
  if (!modalEl || !modalEl.isConnected) {
    modalEl = document.querySelector('[data-coffee-wheel-modal]');
  }
  return modalEl;
}

function toRad(deg) {
  return ((deg - 90) * Math.PI) / 180;
}

function polar(r, deg) {
  const a = toRad(deg);
  return [CX + r * Math.cos(a), CY + r * Math.sin(a)];
}

function arcPath(r0, r1, a0, a1) {
  const large = a1 - a0 > 180 ? 1 : 0;
  const [x0, y0] = polar(r1, a0);
  const [x1, y1] = polar(r1, a1);
  const [x2, y2] = polar(r0, a1);
  const [x3, y3] = polar(r0, a0);
  return [
    `M ${x0.toFixed(2)} ${y0.toFixed(2)}`,
    `A ${r1} ${r1} 0 ${large} 1 ${x1.toFixed(2)} ${y1.toFixed(2)}`,
    `L ${x2.toFixed(2)} ${y2.toFixed(2)}`,
    `A ${r0} ${r0} 0 ${large} 0 ${x3.toFixed(2)} ${y3.toFixed(2)}`,
    'Z',
  ].join(' ');
}

function hsl({ hue, sat, light }, dLight = 0, dSat = 0) {
  return `hsl(${hue} ${Math.max(8, sat + dSat)}% ${Math.min(92, Math.max(8, light + dLight))}%)`;
}

function readLiveCatalogue() {
  descriptorInfo = new Map();
  productsBySlug = new Map();
  productTitles = new Map();
  productDescriptors = new Map();
  const seen = new Set();
  document.querySelectorAll('.coffee-collection__item[data-descriptor]').forEach((card) => {
    if (card.dataset.available !== 'true') return;
    const productId = card.dataset.productId || '';
    if (!productId || seen.has(productId)) return;
    seen.add(productId);
    productTitles.set(productId, shortTitle(card.dataset.productTitle || ''));
    const price = parseFloat(card.dataset.pricePerKg || '');
    productMeta.set(productId, {
      roast: card.dataset.roastStrengthLabel || '',
      price: Number.isFinite(price) ? `$${Math.round(price)}/kg` : '',
      url: card.dataset.productUrl || '',
      // brew style — same default as the grid's getCardExtraction
      extraction: (card.dataset.extraction || '').toLowerCase() === 'filter' ? 'filter' : 'espresso',
    });
    const descs = [];
    (card.dataset.descriptorEntries || '').split('|').forEach((entry) => {
      const idx = entry.indexOf(':');
      if (idx === -1) return;
      const slug = entry.slice(0, idx).trim();
      const label = entry.slice(idx + 1).trim();
      if (!slug) return;
      descs.push({ slug, label });
      if (!productsBySlug.has(slug)) productsBySlug.set(slug, new Set());
      productsBySlug.get(slug).add(productId);
      const info = descriptorInfo.get(slug);
      if (info) info.count += 1;
      else descriptorInfo.set(slug, { label, count: 1, category: '' });
    });
    productDescriptors.set(productId, descs);
  });
  inStockProductCount = document.querySelectorAll(
    '.coffee-collection__item[data-available="true"]'
  ).length;
}

/** Selected slugs grouped by flavour family. */
function selectedByFamily() {
  /** @type {Map<string, string[]>} */
  const byFamily = new Map();
  selected.forEach((slug) => {
    const family = descriptorInfo.get(slug)?.category || slug;
    if (!byFamily.has(family)) byFamily.set(family, []);
    byFamily.get(family).push(slug);
  });
  return byFamily;
}

/** Does this product match the given (or current) roast-type mode? */
function matchesRoast(id, mode = roastMode) {
  if (mode === 'all') return true;
  return (productMeta.get(id)?.extraction || 'espresso') === mode;
}

/**
 * Coffees matching the flavour selection BEFORE roast-type narrowing:
 * OR within a family, AND across families — "any berry" broadens,
 * "berry AND chocolate" narrows.
 * @returns {Set<string> | null} matching product ids, or null when nothing selected
 */
function matchingProductsAnyRoast() {
  if (!selected.size) return null;
  let result = null;
  for (const slugs of selectedByFamily().values()) {
    const union = new Set();
    slugs.forEach((slug) => {
      (productsBySlug.get(slug) || []).forEach((id) => union.add(id));
    });
    result = result === null ? union : new Set([...result].filter((id) => union.has(id)));
  }
  return result || new Set();
}

/**
 * The flavour selection narrowed by the roast-type toggle. Null still means
 * "no filter at all"; with a roast mode active but no flavours picked, the
 * result is every wheel product of that brew style.
 * @returns {Set<string> | null}
 */
function matchingProducts() {
  const base = matchingProductsAnyRoast();
  if (base === null) {
    if (roastMode === 'all') return null;
    const all = new Set();
    productTitles.forEach((_, id) => {
      if (matchesRoast(id)) all.add(id);
    });
    return all;
  }
  if (roastMode === 'all') return base;
  return new Set([...base].filter((id) => matchesRoast(id)));
}

/** Product counts per roast mode for the current flavour selection. */
function roastCounts() {
  const base = matchingProductsAnyRoast();
  const ids = base === null ? [...productTitles.keys()] : [...base];
  const counts = { all: ids.length, espresso: 0, filter: 0 };
  ids.forEach((id) => {
    if ((productMeta.get(id)?.extraction || 'espresso') === 'filter') counts.filter += 1;
    else counts.espresso += 1;
  });
  return counts;
}

function matchingProductCount() {
  const ids = matchingProducts();
  return ids === null ? null : ids.size;
}

/**
 * When no coffee carries a note from every selected family, rank coffees by how
 * many of those families they DO hit — the "closest" partial matches. Same
 * OR-within-family rule: a family counts as hit if the coffee has any of that
 * family's selected notes. Returns best-first
 * `[{ id, families, notes, total }]` (families = groups hit, for the tag; notes
 * = individual selected notes hit, a finer tie-break), excluding exact matches
 * and coffees that hit none of the families.
 */
function closestMatches() {
  const families = [...selectedByFamily().values()];
  const total = families.length;
  if (total < 2) return []; // a single family always yields exact matches
  const familySets = families.map((slugs) => {
    const set = new Set();
    slugs.forEach((slug) => (productsBySlug.get(slug) || []).forEach((id) => set.add(id)));
    return set;
  });
  /** @type {Map<string, { families: number, notes: number }>} */
  const scores = new Map();
  familySets.forEach((set) => {
    set.forEach((id) => {
      const s = scores.get(id) || { families: 0, notes: 0 };
      s.families += 1;
      scores.set(id, s);
    });
  });
  selected.forEach((slug) => {
    (productsBySlug.get(slug) || []).forEach((id) => {
      const s = scores.get(id);
      if (s) s.notes += 1;
    });
  });
  return [...scores.entries()]
    .map(([id, s]) => ({ id, families: s.families, notes: s.notes, total }))
    .filter((r) => r.families > 0 && r.families < total && matchesRoast(r.id))
    .sort((a, b) => b.families - a.families || b.notes - a.notes)
    .slice(0, 24);
}

function coffeesLabel(count) {
  const modal = getModal();
  const template = count === 1 ? modal.dataset.labelCoffee : modal.dataset.labelCoffees;
  return (template || '{count} coffees').replace('{count}', String(count));
}

function updateFooter() {
  const modal = getModal();
  const apply = modal.querySelector('[data-coffee-wheel-apply]');
  // both the footer clear (mobile) and the sidebar clear (desktop) share the hook
  const clears = modal.querySelectorAll('[data-coffee-wheel-clear]');
  // ...as do the two no-match notes (footer on phones, panel on desktop)
  const nomatches = modal.querySelectorAll('[data-coffee-wheel-nomatch]');
  const count = matchingProductCount();
  if (count === null) {
    apply.textContent = modal.dataset.labelShowAll || 'Show all coffees';
    apply.disabled = false;
  } else if (count === 0 && closestMatches().length > 0) {
    // no exact matches but we have partials — the CTA applies those instead
    // of dead-ending on a disabled "Show 0 coffees"
    apply.textContent = modal.dataset.labelShowClosest || 'Show closest matches';
    apply.disabled = false;
  } else {
    apply.textContent = (modal.dataset.labelShow || 'Show {count}').replace(
      '{count}',
      coffeesLabel(count)
    );
    apply.disabled = count === 0;
  }
  clears.forEach((clear) => {
    clear.hidden = count === null;
  });
  // guide out of an impossible combination instead of a bare disabled CTA
  nomatches.forEach((nomatch) => {
    nomatch.hidden = count !== 0;
  });
}

/** Sync every roast-toggle copy (footer + panel): active state + live counts. */
function updateRoastUI() {
  const modal = getModal();
  if (!modal) return;
  const counts = roastCounts();
  modal.querySelectorAll('[data-coffee-wheel-roast] [data-roast-option]').forEach((btn) => {
    const mode = btn.dataset.roastOption;
    const active = mode === roastMode;
    btn.classList.toggle('is-active', active);
    btn.setAttribute('aria-pressed', active ? 'true' : 'false');
    const count = btn.querySelector('[data-roast-count]');
    if (count) count.textContent = String(counts[mode] ?? '');
  });
}

function refreshSelectionStyles(svg) {
  const anySelected = selected.size > 0;
  svg.querySelectorAll('[data-wheel-descriptor]').forEach((seg) => {
    const isSelected = selected.has(seg.dataset.wheelDescriptor);
    seg.classList.toggle('is-selected', isSelected);
    seg.classList.toggle('is-unselected', anySelected && !isSelected);
    seg.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
  });
  svg.querySelectorAll('[data-wheel-category]').forEach((seg) => {
    const slugs = (seg.dataset.wheelDescriptors || '').split('|').filter(Boolean);
    const allSelected = slugs.length > 0 && slugs.every((s) => selected.has(s));
    seg.classList.toggle('is-selected', allSelected);
    seg.setAttribute('aria-pressed', allSelected ? 'true' : 'false');
  });
  updateCentre(svg);
  updateFooter();
  updateRoastUI();
  renderSelectionPanel(svg);
}

/** Side panel (and small-screen footer strip): chips + live top matches. */
function renderSelectionPanel(svg) {
  const modal = getModal();
  const chipTargets = [
    modal.querySelector('[data-coffee-wheel-chips]'),
    modal.querySelector('[data-coffee-wheel-footer-chips]'),
  ].filter(Boolean);
  const emptyHint = modal.querySelector('[data-coffee-wheel-panel-empty]');
  const matchesBlock = modal.querySelector('[data-coffee-wheel-matches-block]');
  const matchesList = modal.querySelector('[data-coffee-wheel-matches]');

  chipTargets.forEach((target) => {
    target.innerHTML = '';
    selected.forEach((slug) => {
      const info = descriptorInfo.get(slug);
      const color = familyColors.get(info?.category);
      const chip = document.createElement('button');
      chip.type = 'button';
      chip.className = 'coffee-wheel-modal__chip';
      if (color) {
        chip.style.setProperty('--chip-bg', hsl(color, 34, -6));
        chip.style.setProperty('--chip-border', hsl(color, 2, -8));
        chip.style.setProperty('--chip-text', hsl(color, -36, 4));
      }
      chip.setAttribute('aria-label', `Remove ${info?.label || slug}`);
      const label = document.createElement('span');
      label.textContent = info?.label || slug;
      chip.appendChild(label);
      const x = document.createElement('span');
      x.className = 'coffee-wheel-modal__chip-x';
      x.setAttribute('aria-hidden', 'true');
      x.textContent = '×';
      chip.appendChild(x);
      chip.addEventListener('click', () => toggleDescriptor(svg, slug));
      target.appendChild(chip);
    });
  });

  if (emptyHint) emptyHint.hidden = selected.size > 0;

  // sidebar chip+Clear row only exists while something is selected
  const selectedRow = modal.querySelector('[data-coffee-wheel-selected-row]');
  if (selectedRow) selectedRow.hidden = selected.size === 0;

  const countHeadline = modal.querySelector('[data-coffee-wheel-panel-count]');
  const selectionHeading = modal.querySelector('[data-coffee-wheel-panel-heading]');
  const ids = matchingProducts();

  if (countHeadline && selectionHeading) {
    if (ids === null) {
      countHeadline.hidden = true;
      selectionHeading.hidden = false;
    } else if (ids.size === 0) {
      countHeadline.textContent = modal.dataset.labelNoExact || 'No exact matches';
      countHeadline.hidden = false;
      selectionHeading.hidden = true;
    } else {
      const template =
        (ids.size === 1 ? modal.dataset.labelCountOne : modal.dataset.labelCount) || '{count} coffees match';
      countHeadline.textContent = template.replace('{count}', String(ids.size));
      countHeadline.hidden = false;
      selectionHeading.hidden = true;
    }
  }

  if (matchesBlock && matchesList) {
    matchesList.innerHTML = '';
    const matchesHeading = matchesBlock.querySelector('[data-coffee-wheel-matches-heading]');

    // Exact matches when we have them; otherwise the closest partial matches.
    /** @type {{ id: string, close: { n: number, total: number } | null }[] | null} */
    let items = null;
    let headingLabel = null;
    let applyMore = true; // "+ N more" applies the selection to the grid...
    if (ids && ids.size > 0) {
      items = [...ids].map((id) => ({ id, close: null }));
      headingLabel = modal.dataset.labelMatches || 'Top matches';
    } else if (ids && ids.size === 0) {
      const close = closestMatches();
      if (close.length) {
        items = close.map((r) => ({ id: r.id, close: { n: r.families, total: r.total } }));
        headingLabel = modal.dataset.labelClosest || 'Closest matches';
        applyMore = false; // ...but for closest that would filter the grid to zero
      }
    }

    if (!items) {
      matchesBlock.hidden = true;
    } else {
      if (matchesHeading && headingLabel) matchesHeading.textContent = headingLabel;
      const panel = modal.querySelector('[data-coffee-wheel-panel]');
      // Never collapse below the original default; taller panels show more.
      const minShown = 5;

      const buildMatch = ({ id, close }) => {
        const li = document.createElement('li');
        li.className = 'coffee-wheel-modal__match';
        const meta = productMeta.get(id);
        const url = meta?.url;
        if (url) li.classList.add('coffee-wheel-modal__match--link');
        // link to the product page when we have a URL; the stretched ::after
        // (see CSS) makes the whole card clickable. Fall back to plain text.
        const title = document.createElement(url ? 'a' : 'span');
        title.className = url
          ? 'coffee-wheel-modal__match-title coffee-wheel-modal__match-link'
          : 'coffee-wheel-modal__match-title';
        if (url) {
          title.href = url;
          // open the product in a new tab so the wheel stays put behind it
          title.target = '_blank';
          title.rel = 'noopener noreferrer';
        }
        title.textContent = productTitles.get(id) || '';
        li.appendChild(title);
        const metaText = [meta?.roast, meta?.price].filter(Boolean).join(' · ');
        const extraction = meta?.extraction;
        if (metaText || extraction) {
          const sub = document.createElement('span');
          sub.className = 'coffee-wheel-modal__match-meta';
          if (extraction === 'espresso' || extraction === 'filter') {
            // brew-style badge so espresso vs filter reads at a glance
            const brew = document.createElement('span');
            brew.className = `coffee-wheel-modal__match-brew coffee-wheel-modal__match-brew--${extraction}`;
            brew.textContent = extraction === 'espresso' ? 'Espresso' : 'Filter';
            sub.appendChild(brew);
          }
          if (metaText) sub.appendChild(document.createTextNode(metaText));
          li.appendChild(sub);
        }
        if (close) {
          // partial match: show how many of the selected families it hits
          li.classList.add('coffee-wheel-modal__match--close');
          const tag = document.createElement('span');
          tag.className = 'coffee-wheel-modal__match-tag';
          const tpl = modal.dataset.labelMatchOf || 'matches {matched} of {total}';
          tag.textContent = tpl
            .replace('{matched}', String(close.n))
            .replace('{total}', String(close.total));
          li.appendChild(tag);
        }
        // flavour notes — the matched ones on a close card, all of them on exact
        const descs = productDescriptors.get(id) || [];
        const notes = close ? descs.filter((d) => selected.has(d.slug)) : descs;
        if (notes.length) {
          const notesWrap = document.createElement('span');
          notesWrap.className = 'coffee-wheel-modal__match-notes';
          notes.forEach((d) => {
            const note = document.createElement('span');
            note.className = 'coffee-wheel-modal__match-note';
            if (close) {
              // tint matched notes by family so they read as "your flavours"
              const color = familyColors.get(descriptorInfo.get(d.slug)?.category);
              if (color) {
                note.classList.add('coffee-wheel-modal__match-note--matched');
                note.style.setProperty('--note-bg', hsl(color, 42, -6));
                note.style.setProperty('--note-text', hsl(color, -32, 2));
              }
            }
            note.textContent = d.label;
            notesWrap.appendChild(note);
          });
          li.appendChild(notesWrap);
        }
        if (url) {
          // decorative "opens product page" cue on the right of the card
          const arrow = document.createElementNS(SVG_NS, 'svg');
          arrow.setAttribute('class', 'coffee-wheel-modal__match-arrow');
          arrow.setAttribute('viewBox', '0 0 16 16');
          arrow.setAttribute('aria-hidden', 'true');
          arrow.setAttribute('focusable', 'false');
          arrow.innerHTML =
            '<path d="M5.5 10.5 10.5 5.5M10.5 5.5H6M10.5 5.5V10" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round"></path>';
          li.appendChild(arrow);
        }
        return li;
      };

      // exact list: "+ N more — show all" (applies the selection); closest list:
      // an action-free "+ N more" note, since applying here would filter to zero.
      const moreLabel = (n) =>
        (applyMore
          ? modal.dataset.labelMore || '+ {count} more'
          : modal.dataset.labelMoreNote || '+ {count} more'
        ).replace('{count}', String(n));

      const buildMore = (n) => {
        const more = document.createElement('li');
        more.className = 'coffee-wheel-modal__match-more';
        if (applyMore) {
          const moreBtn = document.createElement('button');
          moreBtn.type = 'button';
          moreBtn.className = 'coffee-wheel-modal__panel-more';
          moreBtn.textContent = moreLabel(n);
          moreBtn.addEventListener('click', applySelection);
          more.appendChild(moreBtn);
        } else {
          // closest list: "+ N more" is informational (applying would show zero)
          const note = document.createElement('span');
          note.className = 'coffee-wheel-modal__panel-more-note';
          note.textContent = moreLabel(n);
          more.appendChild(note);
        }
        return more;
      };

      // Render every item, then — on desktop, where the panel is a fixed-height
      // scroll region — trim trailing items to whatever fits without scrolling,
      // only falling back to a "+ N more" row when the panel runs out of space.
      items.forEach((item) => matchesList.appendChild(buildMatch(item)));
      matchesBlock.hidden = false;

      const overflowing = () => panel && panel.scrollHeight > panel.clientHeight + 1;
      if (overflowing()) {
        while (matchesList.children.length > minShown && overflowing()) {
          matchesList.removeChild(matchesList.lastChild);
        }
        let shownCount = matchesList.children.length;
        if (shownCount < items.length) {
          const more = buildMore(items.length - shownCount);
          const moreEl = more.firstChild;
          matchesList.appendChild(more);
          // The "more" row itself takes space; drop items until it fits.
          while (shownCount > 1 && overflowing()) {
            matchesList.removeChild(matchesList.children[shownCount - 1]);
            shownCount -= 1;
            moreEl.textContent = moreLabel(items.length - shownCount);
          }
        }
      }
    }
  }
}

function toggleDescriptor(svg, slug) {
  const adding = !selected.has(slug);
  if (adding) selected.add(slug);
  else selected.delete(slug);
  if (adding) selectChime();
  refreshSelectionStyles(svg);
}

function toggleCategory(svg, slugs) {
  const allSelected = slugs.length > 0 && slugs.every((s) => selected.has(s));
  slugs.forEach((s) => (allSelected ? selected.delete(s) : selected.add(s)));
  if (!allSelected && slugs.length) selectChime();
  refreshSelectionStyles(svg);
}

// ---- desktop flavour search (sidebar panel, ≥1200px) ----
/**
 * Flat searchable list of every live outer note + inner family, rebuilt from
 * the same data the wheel draws from so search can never drift from the wheel.
 * @type {{ type: 'descriptor' | 'family', label: string, family: string,
 *   familyLabel: string, slug?: string, slugs?: string[], count: number,
 *   search: string }[]}
 */
let searchIndex = [];
/** Rows currently shown in the dropdown (drives keyboard nav). */
let searchMatches = [];
/** Highlighted dropdown row, or -1 for none. */
let searchActive = -1;

function buildSearchIndex() {
  const famName = new Map();
  categorySegments.forEach((c) => famName.set(c.slug, c.name));
  const entries = [];
  descriptorInfo.forEach((info, slug) => {
    const familyLabel = famName.get(info.category) || '';
    entries.push({
      type: 'descriptor',
      label: info.label,
      family: info.category,
      familyLabel,
      slug,
      count: info.count,
      search: `${info.label} ${familyLabel}`.toLowerCase(),
    });
  });
  categorySegments.forEach((c) => {
    entries.push({
      type: 'family',
      label: c.name,
      family: c.slug,
      familyLabel: c.name,
      slugs: c.slugs,
      count: c.count,
      search: c.name.toLowerCase(),
    });
  });
  return entries;
}

/** Match strength: exact < prefix < substring < family-only; 99 = no match. */
function searchRank(entry, q) {
  const label = entry.label.toLowerCase();
  if (label === q) return 0;
  if (label.startsWith(q)) return 1;
  if (label.includes(q)) return 2;
  if (entry.search.includes(q)) return 3;
  return 99;
}

function filterSearch(raw) {
  const q = raw.trim().toLowerCase();
  if (!q) return [];
  return searchIndex
    .map((entry) => ({ entry, rank: searchRank(entry, q) }))
    .filter((m) => m.rank < 99)
    .sort(
      (a, b) =>
        a.rank - b.rank ||
        b.entry.count - a.entry.count ||
        a.entry.label.localeCompare(b.entry.label)
    )
    .slice(0, 8)
    .map((m) => m.entry);
}

/** Apply a chosen row exactly as a wheel click would — only ever adds. */
function applySearchResult(svg, entry) {
  if (entry.type === 'family') {
    const allSelected = entry.slugs.length > 0 && entry.slugs.every((s) => selected.has(s));
    if (!allSelected) toggleCategory(svg, entry.slugs);
  } else if (!selected.has(entry.slug)) {
    toggleDescriptor(svg, entry.slug);
  }
}

/** Inject + wire the sidebar search field once. Idempotent. */
function ensureSearchUI(svg) {
  const modal = getModal();
  const panel = modal && modal.querySelector('[data-coffee-wheel-panel]');
  if (!panel || panel.querySelector('[data-coffee-wheel-search]')) return;

  const wrap = document.createElement('div');
  wrap.className = 'coffee-wheel-modal__search';
  wrap.setAttribute('data-coffee-wheel-search', '');

  const field = document.createElement('div');
  field.className = 'coffee-wheel-modal__search-field';
  const icon = document.createElementNS(SVG_NS, 'svg');
  icon.setAttribute('class', 'coffee-wheel-modal__search-icon');
  icon.setAttribute('viewBox', '0 0 20 20');
  icon.setAttribute('aria-hidden', 'true');
  icon.innerHTML =
    '<circle cx="9" cy="9" r="6" fill="none" stroke="currentColor" stroke-width="1.6"></circle>' +
    '<line x1="13.5" y1="13.5" x2="18" y2="18" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"></line>';
  field.appendChild(icon);

  const input = document.createElement('input');
  input.type = 'text';
  input.className = 'coffee-wheel-modal__search-input';
  input.setAttribute('data-coffee-wheel-search-input', '');
  input.setAttribute('placeholder', 'Search flavours');
  input.setAttribute('autocomplete', 'off');
  input.setAttribute('spellcheck', 'false');
  input.setAttribute('role', 'combobox');
  input.setAttribute('aria-expanded', 'false');
  input.setAttribute('aria-controls', 'coffee-wheel-search-list');
  input.setAttribute('aria-autocomplete', 'list');
  input.setAttribute('aria-label', 'Search flavours');
  field.appendChild(input);
  wrap.appendChild(field);

  const list = document.createElement('ul');
  list.className = 'coffee-wheel-modal__search-results';
  list.id = 'coffee-wheel-search-list';
  list.setAttribute('role', 'listbox');
  list.setAttribute('data-coffee-wheel-search-results', '');
  list.hidden = true;
  wrap.appendChild(list);

  // sit the search at the top of the panel's interactive area, but below the
  // intro disclaimer (which is the panel's first child in the markup)
  const anchor = panel.querySelector('[data-coffee-wheel-panel-count]') || panel.firstChild;
  panel.insertBefore(wrap, anchor);
  bindSearch(svg, input, list);
}

function bindSearch(svg, input, list) {
  const closeList = () => {
    list.hidden = true;
    list.innerHTML = '';
    searchMatches = [];
    searchActive = -1;
    input.setAttribute('aria-expanded', 'false');
    input.removeAttribute('aria-activedescendant');
  };

  const setActive = (i) => {
    searchActive = i;
    [...list.children].forEach((row, idx) => {
      const on = idx === i;
      row.classList.toggle('coffee-wheel-modal__search-option--active', on);
      if (row.getAttribute('role') === 'option') {
        row.setAttribute('aria-selected', on ? 'true' : 'false');
      }
    });
    const row = i >= 0 ? list.children[i] : null;
    if (row) {
      input.setAttribute('aria-activedescendant', row.id);
      row.scrollIntoView({ block: 'nearest' });
    } else {
      input.removeAttribute('aria-activedescendant');
    }
  };

  const choose = (i) => {
    const entry = searchMatches[i];
    if (!entry) return;
    applySearchResult(svg, entry);
    input.value = '';
    closeList();
    input.focus();
  };

  const renderList = () => {
    list.innerHTML = '';
    if (!searchMatches.length) {
      const empty = document.createElement('li');
      empty.className = 'coffee-wheel-modal__search-empty';
      empty.textContent = 'No flavour found';
      list.appendChild(empty);
    } else {
      searchMatches.forEach((entry, i) => {
        const row = document.createElement('li');
        row.className = 'coffee-wheel-modal__search-option';
        row.id = `coffee-wheel-search-opt-${i}`;
        row.setAttribute('role', 'option');
        row.setAttribute('aria-selected', i === searchActive ? 'true' : 'false');
        if (i === searchActive) row.classList.add('coffee-wheel-modal__search-option--active');

        const color = familyColors.get(entry.family);
        const dot = document.createElement('span');
        dot.className = 'coffee-wheel-modal__search-dot';
        if (color) dot.style.setProperty('--dot', hsl(color, 6, 2));
        row.appendChild(dot);

        const name = document.createElement('span');
        name.className = 'coffee-wheel-modal__search-name';
        name.textContent = entry.label;
        row.appendChild(name);

        const meta = document.createElement('span');
        meta.className = 'coffee-wheel-modal__search-meta';
        const scope = entry.type === 'family' ? 'Family' : entry.familyLabel;
        meta.textContent = [scope, coffeesLabel(entry.count)].filter(Boolean).join(' · ');
        row.appendChild(meta);

        // mousedown (not click) so it fires before the input blurs / outside-close
        row.addEventListener('mousedown', (event) => {
          event.preventDefault();
          choose(i);
        });
        row.addEventListener('mousemove', () => {
          if (searchActive !== i) setActive(i);
        });
        list.appendChild(row);
      });
    }
    list.hidden = false;
    input.setAttribute('aria-expanded', 'true');
  };

  const runQuery = () => {
    if (!input.value.trim()) {
      closeList();
      return;
    }
    searchMatches = filterSearch(input.value);
    searchActive = searchMatches.length ? 0 : -1;
    renderList();
  };

  input.addEventListener('input', runQuery);
  input.addEventListener('focus', () => {
    if (list.hidden && input.value.trim()) runQuery();
  });
  input.addEventListener('keydown', (event) => {
    if (event.key === 'ArrowDown') {
      event.preventDefault();
      if (list.hidden) {
        runQuery();
      } else if (searchMatches.length) {
        setActive((searchActive + 1) % searchMatches.length);
      }
    } else if (event.key === 'ArrowUp') {
      event.preventDefault();
      if (searchMatches.length) {
        setActive((searchActive - 1 + searchMatches.length) % searchMatches.length);
      }
    } else if (event.key === 'Enter') {
      if (!list.hidden && searchActive >= 0) {
        event.preventDefault();
        choose(searchActive);
      }
    } else if (event.key === 'Escape') {
      if (!list.hidden) {
        event.preventDefault();
        event.stopPropagation(); // close the dropdown, not the whole dialog
        closeList();
      }
    }
  });

  // Click outside the search closes the dropdown; the selection is untouched.
  document.addEventListener('mousedown', (event) => {
    if (!list.hidden && !event.target.closest('[data-coffee-wheel-search]')) closeList();
  });
}

function showTooltip(target, title, subtitle, products, accentColor) {
  const modal = getModal();
  const tooltip = modal.querySelector('[data-coffee-wheel-tooltip]');
  tooltip.innerHTML = '';
  tooltip.style.setProperty('--tip-accent', accentColor || '#9b7a5b');
  const head = document.createElement('span');
  head.className = 'coffee-wheel-tooltip__head';
  const dot = document.createElement('span');
  dot.className = 'coffee-wheel-tooltip__dot';
  head.appendChild(dot);
  const strong = document.createElement('strong');
  strong.textContent = title;
  head.appendChild(strong);
  tooltip.appendChild(head);
  const span = document.createElement('span');
  span.textContent = subtitle;
  tooltip.appendChild(span);
  (products || []).forEach((name) => {
    const em = document.createElement('em');
    em.textContent = name;
    tooltip.appendChild(em);
  });
  tooltip.hidden = false;
  const stage = modal.querySelector('.coffee-wheel-modal__stage');
  const stageRect = stage.getBoundingClientRect();
  const rect = target.getBoundingClientRect();
  let left = rect.left + rect.width / 2 - stageRect.left;
  left = Math.min(Math.max(left, 90), stageRect.width - 90);
  let top = rect.top - stageRect.top - 10;
  // too close to the top edge — show the tooltip under the segment instead
  const below = top < 64;
  tooltip.classList.toggle('coffee-wheel-tooltip--below', below);
  if (below) top = rect.bottom - stageRect.top + 12;
  tooltip.style.left = `${Math.round(left)}px`;
  tooltip.style.top = `${Math.round(top)}px`;
}

function hideTooltip() {
  const tooltip = getModal().querySelector('[data-coffee-wheel-tooltip]');
  tooltip.hidden = true;
}

/** family slug -> every element carrying data-wheel-category-of for it */
let familyMemberEls = new Map();
let curDimFamily = null;

/**
 * Dim every family except `category`. Perf-shaped two ways for this page's
 * :has()-heavy stylesheets, where every CLASS change pays a ~9ms selector
 * re-match during a drag:
 * — delta-based: only the two families that changed are touched (~16 nodes),
 *   not all ~90 segments;
 * — inline `opacity` instead of the .is-dimmed class: inline style mutations
 *   are self-invalidating and skip selector matching entirely. The segments'
 *   CSS `transition` still animates the change on desktop hover, and the
 *   `--touching` transition:none keeps it instant mid-drag.
 */
const DIM_OPACITY = '0.35';

function dimEl(el, dim) {
  el.style.opacity = dim ? DIM_OPACITY : '';
}

function setHoverCategory(svg, category) {
  if (category === curDimFamily) return;
  const prev = curDimFamily;
  curDimFamily = category;
  if (category === null || prev === null) {
    // entering/leaving hover entirely — one full sweep is unavoidable
    svg.querySelectorAll('[data-wheel-category-of]').forEach((seg) => {
      dimEl(seg, category !== null && seg.dataset.wheelCategoryOf !== category);
    });
    return;
  }
  (familyMemberEls.get(prev) || []).forEach((el) => dimEl(el, true));
  (familyMemberEls.get(category) || []).forEach((el) => dimEl(el, false));
}

/**
 * Radial label along the segment's mid-angle, flipped on the left half so it
 * always reads outward. `tone` picks dark-on-light vs light-on-dark styling,
 * `tier` picks the full-size or compact font.
 */
function radialLabel(midDeg, text, tone, tier) {
  const rMid = (R_OUTER_0 + R_OUTER_1) / 2;
  const [x, y] = polar(rMid, midDeg);
  const el = document.createElementNS(SVG_NS, 'text');
  const normalized = ((midDeg % 360) + 360) % 360;
  const flip = normalized > 180;
  const rotation = flip ? midDeg + 90 : midDeg - 90;
  el.setAttribute('x', x.toFixed(1));
  el.setAttribute('y', y.toFixed(1));
  el.setAttribute('transform', `rotate(${rotation.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)})`);
  el.setAttribute('text-anchor', 'middle');
  el.setAttribute('dominant-baseline', 'central');
  el.setAttribute(
    'class',
    `coffee-wheel__descriptor-label coffee-wheel__descriptor-label--${tier} coffee-wheel__descriptor-label--${tone}`
  );
  el.textContent = text;
  radialFlipLabels.push({ el, mid: midDeg, flipped: flip });
  return el;
}

/**
 * Category name along the inner ring. The rendered text is measured against
 * its arc; if the full name doesn't fit its short fallback is tried, and if
 * that still overflows it falls back to a radial spoke label.
 */
function categoryArcLabel(svg, defs, index, a0, a1, name, fill) {
  const rText = (R_INNER_0 + R_INNER_1) / 2;
  const arcLen = ((a1 - a0) * Math.PI * rText) / 180;
  const normalizedMid = ((((a0 + a1) / 2) % 360) + 360) % 360;
  const reverse = normalizedMid > 90 && normalizedMid < 270;
  const path = document.createElementNS(SVG_NS, 'path');
  const id = `coffee-wheel-cat-arc-${index}`;
  const r = reverse ? rText - 4.5 : rText + 4.5;
  const [sx, sy] = polar(r, reverse ? a1 : a0);
  const [ex, ey] = polar(r, reverse ? a0 : a1);
  const large = a1 - a0 > 180 ? 1 : 0;
  path.setAttribute(
    'd',
    `M ${sx.toFixed(2)} ${sy.toFixed(2)} A ${r} ${r} 0 ${large} ${reverse ? 0 : 1} ${ex.toFixed(2)} ${ey.toFixed(2)}`
  );
  path.setAttribute('id', id);
  path.setAttribute('fill', 'none');
  defs.appendChild(path);
  const text = document.createElementNS(SVG_NS, 'text');
  text.setAttribute('class', 'coffee-wheel__category-label');
  text.setAttribute('fill', fill);
  const textPath = document.createElementNS(SVG_NS, 'textPath');
  textPath.setAttribute('href', `#${id}`);
  textPath.setAttribute('startOffset', '50%');
  textPath.setAttribute('text-anchor', 'middle');
  text.appendChild(textPath);
  svg.appendChild(text);

  const fitsRendered = () => textPath.getComputedTextLength() <= arcLen - 8;
  textPath.textContent = name;
  if (!fitsRendered()) {
    textPath.textContent = CATEGORY_SHORT_NAMES[name] || name.split(/\s|&/)[0];
  }
  if (fitsRendered()) {
    arcFlipLabels.push({ pathEl: path, a0, a1, rText, reversed: reverse });
    return;
  }
  text.remove();

  // Arc too tight for curved text — label the family along its spoke instead
  const sweep = a1 - a0;
  if (sweep < 3.8) return;
  const midDeg = (a0 + a1) / 2;
  const radial = document.createElementNS(SVG_NS, 'text');
  radial.setAttribute('text-anchor', 'middle');
  radial.setAttribute('dominant-baseline', 'central');
  radial.setAttribute('class', 'coffee-wheel__category-label coffee-wheel__category-label--radial');
  if (sweep < 7) radial.classList.add('coffee-wheel__category-label--tight');
  radial.setAttribute('fill', fill);
  radial.dataset.midDeg = String(midDeg);
  radial.dataset.baseR = String(rText);
  radial.textContent = CATEGORY_SHORT_NAMES[name] || name.split(/\s|&/)[0];
  positionRadialLabel(radial, rText, midDeg);
  const normalized = ((midDeg % 360) + 360) % 360;
  radialFlipLabels.push({ el: radial, mid: midDeg, flipped: normalized > 180 });
  svg.appendChild(radial);
}

function positionRadialLabel(el, r, midDeg) {
  const [x, y] = polar(r, midDeg);
  const normalized = ((midDeg % 360) + 360) % 360;
  const rotation = normalized > 180 ? midDeg + 90 : midDeg - 90;
  el.setAttribute('x', x.toFixed(1));
  el.setAttribute('y', y.toFixed(1));
  el.setAttribute('transform', `rotate(${rotation.toFixed(1)} ${x.toFixed(1)} ${y.toFixed(1)})`);
}

function liftVars(el, midDeg, distance) {
  const a = toRad(midDeg);
  el.style.setProperty('--lift-x', `${(distance * Math.cos(a)).toFixed(2)}px`);
  el.style.setProperty('--lift-y', `${(distance * Math.sin(a)).toFixed(2)}px`);
}

function segmentInteractions(seg, svg, { category, onActivate, tooltip }) {
  seg.addEventListener('pointerenter', (event) => {
    // touch feedback (hint pill, centre echo, trill) is owned entirely by the
    // drag controller — running the hover path too duplicates the trill and
    // parks the docked tooltip card over the mobile stage for no benefit
    if (event.pointerType !== 'mouse') return;
    setHoverCategory(svg, category);
    showTooltip(seg, tooltip.title, tooltip.subtitle, tooltip.products, tooltip.color);
    updateCentreHover(svg, tooltip.title, tooltip.subtitle);
    trill(Number(seg.dataset.trill) || 780, 0.0272, 0.08);
  });
  seg.addEventListener('pointerleave', (event) => {
    if (event.pointerType !== 'mouse') return;
    setHoverCategory(svg, null);
    hideTooltip();
    updateCentre(svg);
  });
  seg.addEventListener('focus', () => {
    // mobile uses the drag hint, not the focus tooltip (which else docks as an
    // empty pill when the dialog auto-focuses a segment on open)
    if (isCoarsePointer()) return;
    showTooltip(seg, tooltip.title, tooltip.subtitle, tooltip.products, tooltip.color);
  });
  seg.addEventListener('blur', () => {
    hideTooltip();
    updateCentre(svg);
  });
  seg.addEventListener('click', (event) => {
    // Touch is owned by the drag controller (initWheelDragSelect), which
    // handles tap + drag + release select. Let mouse/keyboard use click.
    if (event.pointerType === 'touch' || event.pointerType === 'pen') return;
    onActivate();
  });
  seg.addEventListener('keydown', (event) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    event.preventDefault();
    onActivate();
  });
}

// ---- mobile: roll a thumb around the wheel, lift to select ----

function isCoarsePointer() {
  return window.matchMedia('(pointer: coarse)').matches;
}

/**
 * Snapshot the wheel's client<->viewBox mapping once per gesture so the drag
 * loop never has to call getBoundingClientRect (a layout read) on every move.
 * Captured with the wheel at its natural scale; the micro-zoom is folded back
 * in analytically by mapClientToWheel, so this stays valid for the whole drag.
 */
function computeWheelGeom(svg) {
  const rect = svg.getBoundingClientRect();
  const vb = svg.viewBox.baseVal;
  if (!rect.width || !rect.height || !vb.width) return null;
  const scale = Math.min(rect.width / vb.width, rect.height / vb.height);
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    cx: rect.width / 2, // element centre in local px (camera scales about here)
    cy: rect.height / 2,
    offX: (rect.width - vb.width * scale) / 2,
    offY: (rect.height - vb.height * scale) / 2,
    scale,
    vbX: vb.x,
    vbY: vb.y,
  };
}

/**
 * Map a client point to wheel-space angle (deg, 0 = top) + radius using cached
 * geometry. `scale`/`panX`/`panY` describe the camera transform currently
 * applied to the wheel — translate3d(panX,panY) then scale about the element
 * centre. Inverting it here keeps hit-testing matched to what is rendered under
 * the finger without re-reading layout.
 */
function mapClientToWheel(geom, scale, panX, panY, clientX, clientY) {
  if (!geom) return null;
  // undo translate + scale-about-centre → element-local px under the finger
  const px = geom.cx + (clientX - geom.left - panX - geom.cx) / scale;
  const py = geom.cy + (clientY - geom.top - panY - geom.cy) / scale;
  const ux = geom.vbX + (px - geom.offX) / geom.scale;
  const uy = geom.vbY + (py - geom.offY) / geom.scale;
  const dx = ux - CX;
  const dy = uy - CY;
  let deg = (Math.atan2(dy, dx) * 180) / Math.PI + 90;
  deg = ((deg % 360) + 360) % 360;
  return { deg, radius: Math.hypot(dx, dy) };
}

/** @returns {{ type:'outer'|'category', seg:object } | null} */
function hitTestPolar(deg, radius) {
  // generous outward tolerance so rolling a thumb past the rim still counts
  if (radius >= R_INNER_1 + 1 && radius <= R_OUTER_1 + 46) {
    const seg = outerSegments.find((s) => deg >= s.a0 && deg < s.a1);
    if (seg) return { type: 'outer', seg };
  }
  if (radius >= R_INNER_0 - 4 && radius <= R_INNER_1) {
    const seg = categorySegments.find((s) => deg >= s.a0 && deg < s.a1);
    if (seg) return { type: 'category', seg };
  }
  return null;
}

/** @returns {{ type:'outer'|'category', seg:object } | null} */
function hitTestGeom(geom, scale, panX, panY, clientX, clientY) {
  const p = mapClientToWheel(geom, scale, panX, panY, clientX, clientY);
  if (!p) return null;
  return hitTestPolar(p.deg, p.radius);
}

function stageEl() {
  return getModal().querySelector('.coffee-wheel-modal__stage');
}

function ensureDragHint() {
  const stage = stageEl();
  let hint = stage.querySelector('[data-coffee-wheel-drag-hint]');
  if (!hint) {
    hint = document.createElement('div');
    hint.className = 'coffee-wheel-drag-hint';
    hint.setAttribute('data-coffee-wheel-drag-hint', '');
    hint.hidden = true;
    // permanent text node: updates write .data (a characterData mutation) so
    // Shopify's storefront feature autoloader — a childList MutationObserver
    // that re-scans the DOM on every added node — stays asleep during drags
    hint.appendChild(document.createTextNode(''));
    stage.appendChild(hint);
  }
  return hint;
}

// Mobile-only "dial" camera. Grabbing the ring zooms HARD and swings the
// touched slice into ONE fixed reading window on the right at the horizontal
// midline (3 o'clock) — there the band runs vertically and every label sits
// horizontal, so the magnified view reads like a list and feels identical no
// matter where on the wheel you grab. Finger movement then rotates the wheel
// so segments scroll through the stationary window; lifting selects whatever
// is in it. A quick tap never engages the camera at all — it just selects.
// The pan lerps from rest to the fixed framing on zoom progress, so grab,
// scrolling, release, and grab-during-unwind are all smooth and self-clamped.
const WINDOW_DEG = 90; // the reading window's wheel angle: 3 o'clock, labels horizontal
const WHEEL_ZOOM = 3.0; // window zoom — the fixed framing keeps the band visible, so go big
const CAM_EASE = 0.34; // per-frame ease zooming in (~150ms; crispness arrives early)
const CAM_EASE_OUT = 0.34; // ease home on release (~230ms, retraces the grab glide)
const CAM_FOLLOW = 0.5; // per-frame ease of the wheel rotation toward its target
const SCROLL_DIR = 1; // 1 = content follows the finger (scroll feel); -1 = window advances
let TAP_MS = 130; // press shorter than this (and stiller than TAP_PX) = a plain tap
const TAP_PX = 6;

function initWheelDragSelect(svg) {
  if (svg.dataset.dragBound === 'true') return;
  svg.dataset.dragBound = 'true';

  const hint = ensureDragHint();
  const canZoom = isCoarsePointer() && !prefersReducedMotion();
  // Camera transforms go on the HTML wrapper, NOT the svg: Blink re-lays-out
  // the entire SVG subtree (~87 objects) every frame when an <svg> root's
  // transform changes, while an HTML div takes the composited fast path.
  const camEl = svg.closest('[data-coffee-wheel-cam]') || svg;

  let activePointer = null;
  let pending = null;
  let lastKey = '';
  let lastTrillAt = 0;

  // cached per-gesture geometry — set on pointerdown, read every frame
  let geom = null;
  let stageLeft = 0;
  let stageTop = 0;
  let stageW = 0;
  let hintHalf = 0; // measured only when the hint text changes, not every move

  // incremental hover state — avoids querySelectorAll on every move
  let hotEl = null;
  let hotLabelEl = null;
  let curFamily = null;

  // camera state (eased toward targets each frame)
  let curScale = 1;
  let tgtScale = 1;
  let curPanX = 0;
  let curPanY = 0;
  // dial state: the reading window is FIXED at WINDOW_DEG (it never chases
  // the finger); the grab spins the touched slice into it, then finger
  // movement rotates the wheel. panFinal is the pan that places the band at
  // the window at full zoom; per-frame pan is zoomProgress · panFinal.
  let panFinalX = 0;
  let panFinalY = 0;
  let anchorClientX = 0; // the window position in client px — pins the hint pill
  let anchorClientY = 0;
  let downDeg = 0; // grabbed content angle in natural wheel space
  let downRadius = 0; // grabbed band radius (dial scrolls angle only)
  let lastFingerAng = 0; // unwrap accumulator for the finger's angle
  let curR = 0; // wheel rotation (deg), eased toward tgtR
  let tgtR = 0;
  let spinning = false; // true during the grab swing into the window
  let camMoving = false; // true while scale or rotation is still converging

  // tap state: a short, still press selects instantly with NO camera at all —
  // zoom-blooming a 100ms tap read as glitch, not feedback
  let engaged = false; // camera engaged for this gesture
  let engageTimer = 0;
  let downX = 0;
  let downY = 0;

  // rAF loop state — pointermove only stashes coords; work runs once per frame
  let rafId = 0;
  let fingerX = 0;
  let fingerY = 0;
  let hasFinger = false;
  let dirty = false;
  let camActive = false;

  // Hot-slice emphasis is applied as INLINE styles, not classes: on this page
  // (:has()-heavy stylesheets) every class toggle costs a ~9ms selector
  // re-match, while inline mutations self-invalidate in ~0.1ms. Touch-only
  // path, so the desktop hover/selected CSS classes are untouched.
  const setHot = (el) => {
    if (hotEl === el) return;
    if (hotEl) {
      hotEl.style.transform = '';
      hotEl.style.stroke = '';
      hotEl.style.strokeWidth = '';
    }
    hotEl = el || null;
    if (hotEl) {
      // lift along the slice's own spoke + a crisp light edge (mirrors the old
      // .coffee-wheel__segment--drag-hot rule; keep the two in step by eye)
      hotEl.style.transform = 'translate(var(--lift-x, 0px), var(--lift-y, 0px)) scale(1.06)';
      hotEl.style.stroke = '#fffdf9';
      hotEl.style.strokeWidth = '2.4';
    }
  };

  // subtly emphasise the hovered slice's on-wheel label so it reads at a glance
  const setHotLabel = (el) => {
    if (hotLabelEl === el) return;
    if (hotLabelEl) hotLabelEl.style.fontWeight = '';
    hotLabelEl = el || null;
    if (hotLabelEl) hotLabelEl.style.fontWeight = '800';
  };

  const setFamily = (family) => {
    const next = family || null;
    if (curFamily === next) return;
    curFamily = next;
    setHoverCategory(svg, next); // one class sweep, only when the family changes
  };

  // transform-only positioning, edge-aware so the pill never clips the modal
  const moveHint = (clientX, clientY) => {
    let left = clientX - stageLeft;
    left = Math.min(Math.max(left, 10 + hintHalf), stageW - 10 - hintHalf);
    // sit clear above the fingertip; near the top edge, pin to the edge rather
    // than flipping below the finger (the hand would cover it there)
    let top = clientY - stageTop - 80;
    if (top < 6) top = 6;
    hint.style.transform = `translate(${Math.round(left)}px, ${Math.round(top)}px) translateX(-50%)`;
  };

  const resetHover = () => {
    setFamily(null);
    setHot(null);
    setHotLabel(null);
    hint.hidden = true;
    pending = null;
    if (lastKey !== '') {
      lastKey = '';
      updateCentre(svg); // restore the live match count in the centre
    }
  };

  /** Apply hover feedback for a hit; the pill pins at (pillX, pillY). */
  const applyHit = (hit, pillX, pillY) => {
    if (!hit) {
      resetHover();
      return;
    }
    const isOuter = hit.type === 'outer';
    const key = `${hit.type}:${hit.seg.slug}`;
    pending = hit;

    hint.hidden = false;
    if (key !== lastKey) {
      const text = `${hit.seg.name} · ${coffeesLabel(hit.seg.count)}`;
      hint.firstChild.data = text; // .data, NOT textContent — see ensureDragHint
      // estimated half-width for the edge clamp — an offsetWidth read here is a
      // forced layout on EVERY slice change, which the trace showed janking
      // throttled devices; ~8.3px/char at 15px/700 is within a few px of true
      hintHalf = (text.length * 8.3 + 29) / 2;
    }
    moveHint(pillX, pillY);

    if (key !== lastKey) {
      lastKey = key;
      const family = isOuter ? descriptorInfo.get(hit.seg.slug)?.category : hit.seg.slug;
      setFamily(family);
      setHot(isOuter ? descriptorEls.get(hit.seg.slug) : categoryEls.get(hit.seg.slug));
      setHotLabel(isOuter ? descriptorLabelEls.get(hit.seg.slug) : null);
      // NOTE: no centre echo here — at 3× the centre is out of view, the pill
      // + bold label carry the readout, and rebuilding centre tspans per slice
      // change was a measurable style-recalc cost mid-drag.
      // throttle so a fast thumb-roll across many slices doesn't machine-gun
      const now = performance.now();
      if (now - lastTrillAt > 45) {
        lastTrillAt = now;
        const idx = isOuter ? outerSegments.indexOf(hit.seg) : categorySegments.indexOf(hit.seg);
        trill(pentatonicFreq(Math.max(0, idx)), 0.0272, 0.08);
      }
    }
  };

  const process = (clientX, clientY) => {
    if (canZoom) {
      if (!engaged || spinning) return; // grabbed slice stays hot until the swing lands
      // fixed window: the wheel rotated by curR, so the window at WINDOW_DEG
      // shows whatever started at (WINDOW_DEG − curR) — pure arithmetic, the
      // highlight and the render can never disagree
      const deg = (((WINDOW_DEG - curR) % 360) + 360) % 360;
      applyHit(hitTestPolar(deg, downRadius), anchorClientX, anchorClientY);
    } else {
      // no zoom (reduced motion): classic under-the-finger hit-testing
      applyHit(hitTestGeom(geom, curScale, curPanX, curPanY, clientX, clientY), clientX, clientY);
    }
  };

  // Fixed-window "dial" camera. The pan invariant pan = (1 − scale) · anchor
  // pins the FROZEN touch point while the wheel zooms hard around it; finger
  // movement then ROTATES the wheel about its centre so segments scroll
  // through the stationary magnifier window like a list (content follows the
  // finger). Zoom-in, scrolling, and the release unwind are all the same two
  // eased values (scale + rotation) — nothing to fight, nothing to drift.
  // dtN normalises the per-frame ease factors to real elapsed time, so the
  // durations feel identical on 30Hz phones and 120Hz ones.
  let lastFrameAt = 0;
  const applyCamera = () => {
    const now = performance.now();
    const dtN = lastFrameAt ? Math.min(4, (now - lastFrameAt) / 16.7) : 1;
    lastFrameAt = now;
    // zooming in is deliberate; easing home on release is snappier (~230ms)
    const k = tgtScale < curScale ? CAM_EASE_OUT : CAM_EASE;
    curScale += (tgtScale - curScale) * (1 - Math.pow(1 - k, dtN));
    if (Math.abs(tgtScale - curScale) < 0.002) curScale = tgtScale;
    if (!canZoom) {
      curPanX = 0;
      curPanY = 0;
      camMoving = false;
      return;
    }
    if (hasFinger && geom && engaged) {
      // finger angle about the wheel centre, unwrapped into a running total —
      // circling the dial keeps scrolling smoothly across the ±180° seam
      const ang =
        (Math.atan2(fingerY - geom.top - geom.cy, fingerX - geom.left - geom.cx) * 180) / Math.PI;
      let delta = ang - lastFingerAng;
      if (delta > 180) delta -= 360;
      else if (delta < -180) delta += 360;
      lastFingerAng = ang;
      tgtR += delta * SCROLL_DIR;
    }
    // the grab swing glides (~250ms) — a 180° arrival at scroll speed felt
    // violent; scrolling keeps the tighter follow so the dial stays connected
    const kf = 1 - Math.pow(1 - (spinning ? 0.24 : CAM_FOLLOW), dtN);
    curR += (tgtR - curR) * kf;
    if (Math.abs(tgtR - curR) < 0.02) curR = tgtR;
    // the grab swing has landed once the rotation is close — scrolling takes over
    if (spinning && Math.abs(tgtR - curR) < 2) spinning = false;
    // pan lerps with zoom progress: rendered window content = t·T + (1−t)·c0,
    // a straight glide from the touch point to the framed window and back
    const t = (curScale - 1) / (WHEEL_ZOOM - 1);
    curPanX = t * panFinalX;
    curPanY = t * panFinalY;
    camMoving = curScale !== tgtScale || curR !== tgtR;
    camEl.style.transform = `translate3d(${curPanX.toFixed(2)}px, ${curPanY.toFixed(2)}px, 0) scale(${curScale.toFixed(4)}) rotate(${curR.toFixed(3)}deg)`;
  };

  const cleanup = () => {
    camActive = false;
    hasFinger = false;
    svg.classList.remove('coffee-wheel--touching'); // filter transitions may resume
    camEl.style.transform = '';
    camEl.style.transformOrigin = '';
    camEl.style.willChange = '';
    svg.style.filter = ''; // restore the drop-shadow
    restoreLabelFlipsChunked(); // rest orientation, spread over idle slices
  };

  // Windowed label flips every couple of degrees — skipped entirely while
  // the wheel is flying (orientation is unreadable mid-flick and each write
  // costs a raster invalidation); they catch up the moment scrolling slows.
  // The full rest-orientation restore runs chunked from cleanup().
  let lastFlipR = 0;
  let prevFlipCurR = 0;
  const maybeFlipLabels = () => {
    const vel = Math.abs(curR - prevFlipCurR);
    prevFlipCurR = curR;
    if (curR === lastFlipR) return;
    if (vel > 4) return; // flying — catch up when the scroll slows
    if (curR !== tgtR && Math.abs(curR - lastFlipR) < 2) return;
    lastFlipR = curR;
    updateLabelFlips(curR);
  };

  const loop = () => {
    rafId = 0;
    if (!camActive) return;
    if (dirty || camMoving) {
      dirty = false;
      applyCamera();
      if (canZoom) maybeFlipLabels();
      if (activePointer !== null) process(fingerX, fingerY);
    }
    if (activePointer !== null) {
      // keep animating while the camera converges or a fresh move is pending;
      // otherwise idle until the next pointermove reschedules us
      if (camMoving || dirty) rafId = requestAnimationFrame(loop);
    } else if (camMoving) {
      rafId = requestAnimationFrame(loop); // easing home after release
    } else {
      cleanup(); // settled back at identity
    }
  };
  const ensureLoop = () => {
    if (!rafId && camActive) rafId = requestAnimationFrame(loop);
  };

  /** @param {boolean} select — true on lift (pointerup), false on abort (cancel) */
  const finish = (select) => {
    if (activePointer === null) return;
    activePointer = null;
    hasFinger = false; // freeze the anchor where the finger lifted
    hint.hidden = true;
    setHot(null);
    setHotLabel(null);
    setFamily(null);
    const target = pending;
    pending = null;
    lastKey = '';
    spinning = false;
    // ease the camera home; the loop clears inline styles once it settles.
    // The zoom-out pivots around the frozen anchor, and the rotation unwinds
    // by the shortest path (fold multi-turn spins into ±180° first).
    tgtScale = 1;
    curR = ((curR % 360) + 540) % 360 - 180;
    tgtR = 0;
    camMoving = true; // retargeted — wake the loop even if it had settled
    if (canZoom && camActive) {
      ensureLoop();
    } else {
      cleanup();
    }
    if (select && target) {
      if (target.type === 'outer') toggleDescriptor(svg, target.seg.slug);
      else toggleCategory(svg, target.seg.slugs);
    } else {
      updateCentre(svg);
    }
  };

  // Shield the gesture from the page: theme/app scripts (link prefetchers,
  // wholesale widgets) run document-level pointer/touch listeners that do
  // querySelector work per event — measurably janky at ~70 moves/sec on weak
  // phones. The wheel owns its touches outright inside a modal dialog, so
  // nothing legitimate is listening upstream.
  svg.addEventListener('touchstart', (event) => event.stopPropagation(), { passive: true });
  svg.addEventListener('touchmove', (event) => event.stopPropagation(), { passive: true });

  /**
   * Engage the camera: zoom hard and swing the grabbed slice into the fixed
   * reading window. Deferred from pointerdown so a quick tap never zooms.
   */
  const engage = () => {
    if (activePointer === null || engaged || !geom) return;
    // nothing selectable was grabbed (dead centre / blank stage) — zooming
    // into nothing reads as a glitch, so the camera stays put
    if (!pending) return;
    engaged = true;
    spinning = true;
    cancelLabelFlipRestore(); // a restore mid-flight targets rot 0 — stale now
    // gesture-scoped: segments animate transform only (no filter transitions,
    // which re-raster on every segment change and jank old phones mid-drag)
    svg.classList.add('coffee-wheel--touching');
    // the window and the band mid it magnifies both sit at WINDOW_DEG, so the
    // framing is one constant pan per band: window inset from the stage edge
    // so the magnified rim never clips
    const bandMid =
      downRadius <= R_INNER_1 ? (R_INNER_0 + R_INNER_1) / 2 : (R_OUTER_0 + R_OUTER_1) / 2;
    const rimPx = (R_OUTER_1 - bandMid) * geom.scale * WHEEL_ZOOM;
    const stageHalf = Math.min(geom.width, geom.height) / 2;
    const rT = Math.max(0, stageHalf - rimPx - 10);
    panFinalX = rT - WHEEL_ZOOM * bandMid * geom.scale;
    panFinalY = 0;
    // the hint pill pins at the TOP of the stage — a fixed heads-up readout
    // that never covers the flavour list scrolling through the window
    anchorClientX = stageLeft + stageW / 2;
    anchorClientY = stageTop + 88; // moveHint sits the pill 80px above this → top 8px
    // swing the grabbed slice into the window by the shortest path
    const spin = (((WINDOW_DEG - downDeg - curR) % 360) + 540) % 360 - 180;
    tgtR = curR + spin;
    // pre-orient the window zone for where the swing LANDS — the velocity
    // gate skips flips during the fast swing, and a label righting itself
    // just after arrival read as a glitch
    updateLabelFlips(tgtR);
    tgtScale = WHEEL_ZOOM;
    camEl.style.transformOrigin = '50% 50%'; // rotate/scale about the wheel centre
    // Drop the drop-shadow filter for the gesture: scaling a filtered element
    // re-rasterises the shadow every frame, stuttering weak phones. It's
    // imperceptible mid-gesture and is restored on release.
    svg.style.filter = 'none';
    camEl.style.willChange = 'transform';
    camActive = true;
    camMoving = true;
    dirty = true;
    // the grabbed slice's pill + highlight ride the swing into the window
    applyHit(pending, anchorClientX, anchorClientY);
    ensureLoop();
  };

  svg.addEventListener('pointerdown', (event) => {
    if (event.pointerType === 'mouse') return; // desktop keeps click + hover
    event.stopPropagation();
    if (activePointer !== null) return; // ignore a second finger mid-drag
    activePointer = event.pointerId;
    userEngaged = true;
    dismissCoach();
    try {
      svg.setPointerCapture(event.pointerId);
    } catch (_) {
      // capture unsupported — the angle/radius math still works
    }
    event.preventDefault();

    if (rafId) {
      cancelAnimationFrame(rafId);
      rafId = 0;
    }
    // Geometry: snapshot at rest. If a previous gesture is still easing home,
    // take over from its CURRENT eased scale/rotation instead of snapping the
    // wheel to rest first — the hard reset read as a glitch on quick
    // successive touches. (geom stays valid: the modal can't move mid-open.)
    if (!camActive) {
      camEl.style.transform = ''; // ensure identity before measuring
      geom = computeWheelGeom(svg);
      curScale = 1;
      curPanX = 0;
      curPanY = 0;
      curR = 0;
      tgtR = 0;
    }
    const sr = stageEl().getBoundingClientRect();
    stageLeft = sr.left;
    stageTop = sr.top;
    stageW = sr.width;

    fingerX = event.clientX;
    fingerY = event.clientY;
    downX = event.clientX;
    downY = event.clientY;
    hasFinger = true;
    hotEl = null;
    curFamily = null;
    lastKey = '';
    lastFrameAt = 0; // fresh dt baseline for the time-normalised ease
    spinning = false;
    engaged = false;

    if (geom) {
      lastFingerAng =
        (Math.atan2(fingerY - geom.top - geom.cy, fingerX - geom.left - geom.cx) * 180) / Math.PI;
      // what was grabbed — while a previous gesture eases home the wheel is
      // still rotated by curR, so subtract it to reach natural wheel space
      const p0 = mapClientToWheel(geom, curScale, curPanX, curPanY, fingerX, fingerY);
      downDeg = p0 ? (((p0.deg - curR) % 360) + 360) % 360 : 0;
      downRadius = p0 ? p0.radius : 0;
      // a quick tap selects this directly; a hold/drag swings it into the window
      pending = hitTestPolar(downDeg, downRadius);
    }

    if (canZoom) {
      window.clearTimeout(engageTimer);
      engageTimer = window.setTimeout(engage, TAP_MS);
    } else {
      // reduced motion: no camera — classic under-the-finger tracking
      camActive = true;
      dirty = true;
      ensureLoop();
    }
  });

  svg.addEventListener('pointermove', (event) => {
    if (event.pointerId !== activePointer) return;
    event.preventDefault();
    event.stopPropagation();
    fingerX = event.clientX;
    fingerY = event.clientY;
    // real movement inside the tap window means a drag — engage immediately
    if (canZoom && !engaged && Math.hypot(fingerX - downX, fingerY - downY) > TAP_PX) {
      window.clearTimeout(engageTimer);
      engage();
    }
    dirty = true;
    ensureLoop();
  });

  svg.addEventListener('pointerup', (event) => {
    if (event.pointerId !== activePointer) return;
    event.preventDefault();
    window.clearTimeout(engageTimer); // a quick tap selects with no camera
    finish(true);
  });
  svg.addEventListener('pointercancel', (event) => {
    if (event.pointerId !== activePointer) return;
    window.clearTimeout(engageTimer);
    finish(false); // interrupted gesture (call, system swipe) — don't select
  });

  // Safety nets: never leave the wheel stuck zoomed. If pointer capture
  // failed (old browsers) the lift can land outside the svg; app switches /
  // incoming calls can hide the page without a cancel reaching the element.
  // When capture works these never fire for handled gestures — the svg
  // handlers null activePointer first, so the id test fails here.
  window.addEventListener('pointerup', (event) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    window.clearTimeout(engageTimer);
    finish(true);
  });
  window.addEventListener('pointercancel', (event) => {
    if (activePointer === null || event.pointerId !== activePointer) return;
    window.clearTimeout(engageTimer);
    finish(false);
  });
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden' && activePointer !== null) {
      window.clearTimeout(engageTimer);
      finish(false);
    }
  });
}

// ---- first-run mobile coach ----
let coachTimer = 0;
let userEngaged = false; // suppress the coach if the user already interacted

function ensureCoach() {
  const stage = stageEl();
  let coach = stage.querySelector('[data-coffee-wheel-coach]');
  if (!coach) {
    coach = document.createElement('div');
    coach.className = 'coffee-wheel-coach';
    coach.setAttribute('data-coffee-wheel-coach', '');
    coach.hidden = true;
    coach.innerHTML =
      '<span class="coffee-wheel-coach__orbit"><span class="coffee-wheel-coach__finger"></span></span>' +
      '<p class="coffee-wheel-coach__text">Drag around the wheel — lift your finger to pick a flavour</p>';
    stage.appendChild(coach);
  }
  return coach;
}

function maybeShowCoach() {
  // Mobile tutorial: shown every time the modal opens (no storage suppression),
  // unless the user already started dragging. Reduced-motion users still get it,
  // but the CSS disables the orbit animation so it reads as a static hint.
  if (!isCoarsePointer() || userEngaged) return;
  const coach = ensureCoach();
  coach.hidden = false;
  window.clearTimeout(coachTimer);
  coachTimer = window.setTimeout(dismissCoach, prefersReducedMotion() ? 4200 : 5400);
}

function dismissCoach() {
  const modal = getModal();
  const coach = modal && modal.querySelector('[data-coffee-wheel-coach]');
  window.clearTimeout(coachTimer);
  if (!coach || coach.hidden) return;
  coach.hidden = true;
}

/**
 * Prepare a wheel build: read the live catalogue, compute family geometry,
 * and return one build step per family plus a finish(). Callers either run
 * everything synchronously (open path — the user is waiting) or spread the
 * steps across idle deadlines (prebuild path — never freeze a scrolling
 * phone with one multi-second task).
 */
function startWheelBuild() {
  const modal = getModal();
  const svg = modal.querySelector('[data-coffee-wheel-svg]');
  svg.innerHTML = '';
  // crop tight to the circle so the wheel fills its stage
  const pad = 10;
  svg.setAttribute(
    'viewBox',
    `${CX - R_OUTER_1 - pad} ${CY - R_OUTER_1 - pad} ${(R_OUTER_1 + pad) * 2} ${(R_OUTER_1 + pad) * 2}`
  );
  const defs = document.createElementNS(SVG_NS, 'defs');
  svg.appendChild(defs);
  let segIndex = 0;
  outerSegments = [];
  categorySegments = [];
  descriptorEls = new Map();
  categoryEls = new Map();
  descriptorLabelEls = new Map();
  familyMemberEls = new Map();
  curDimFamily = null;
  radialFlipLabels = [];
  arcFlipLabels = [];
  buildCpuMs = 0;

  readLiveCatalogue();

  // keep only descriptors that exist in the live shop, in taxonomy order
  const cats = taxonomy.categories
    .map((cat) => ({
      ...cat,
      descriptors: cat.descriptors
        .filter((d) => descriptorInfo.has(d.slug))
        .map((d) => ({ ...d, count: descriptorInfo.get(d.slug).count })),
    }))
    .filter((cat) => cat.descriptors.length > 0);

  familyColors = new Map();
  cats.forEach((cat) => {
    familyColors.set(cat.slug, cat.color);
    cat.descriptors.forEach((d) => {
      descriptorInfo.get(d.slug).category = cat.slug;
    });
  });

  // Family sweeps: every family gets at least MIN_FAMILY_ANGLE (or the room
  // its descriptors minimally need); the remaining arc is shared by count.
  const familyWeights = cats.map((c) => c.descriptors.reduce((n, d) => n + d.count, 0));
  const familyMins = cats.map((c) =>
    Math.max(MIN_FAMILY_ANGLE, c.descriptors.length * MIN_DESCRIPTOR_ANGLE)
  );
  const spareArc = Math.max(0, ARC_TOTAL - familyMins.reduce((a, b) => a + b, 0));
  const totalWeight = familyWeights.reduce((a, b) => a + b, 0) || 1;
  const familySweeps = cats.map(
    (c, i) => familyMins[i] + spareArc * (familyWeights[i] / totalWeight)
  );

  let angle = ARC_START;
  /** filled lazily by buildFamily; label step i always runs after family i */
  const labelJobs = [];
  const buildFamily = (cat, catIndex) => {
    const catStart = angle;
    const catSlugs = cat.descriptors.map((d) => d.slug);
    const famEls = [];
    familyMemberEls.set(cat.slug, famEls);
    const innerVariable = familySweeps[catIndex] - cat.descriptors.length * MIN_DESCRIPTOR_ANGLE;
    const familyWeight = familyWeights[catIndex] || 1;
    cat.descriptors.forEach((d, i) => {
      const sweep = MIN_DESCRIPTOR_ANGLE + (d.count / familyWeight) * innerVariable;
      const a0 = angle;
      const a1 = angle + sweep;
      angle = a1;
      const mid = (a0 + a1) / 2;
      const ramp = cat.descriptors.length > 1 ? i / (cat.descriptors.length - 1) : 0;
      const fillColor = hsl(cat.color, 8 + ramp * 20, 4);
      const seg = document.createElementNS(SVG_NS, 'path');
      seg.setAttribute('d', arcPath(R_OUTER_0, R_OUTER_1, a0, a1));
      seg.setAttribute('fill', fillColor);
      seg.setAttribute('class', 'coffee-wheel__segment coffee-wheel__segment--descriptor');
      seg.setAttribute('role', 'button');
      seg.setAttribute('tabindex', '0');
      seg.setAttribute('aria-pressed', 'false');
      seg.setAttribute(
        'aria-label',
        `${d.name} — ${coffeesLabel(d.count)}`
      );
      seg.dataset.wheelDescriptor = d.slug;
      seg.dataset.wheelCategoryOf = cat.slug;
      const segOrder = segIndex++;
      seg.style.setProperty('--seg-i', String(segOrder));
      seg.style.setProperty('--seg-glow', hsl(cat.color, 4, 10).replace(')', ' / 0.38)'));
      seg.dataset.trill = pentatonicFreq(segOrder).toFixed(1);
      liftVars(seg, mid, 8);
      const previewIds = [...(productsBySlug.get(d.slug) || [])];
      const products =
        previewIds.length <= 3 ? previewIds.map((id) => productTitles.get(id)).filter(Boolean) : [];
      segmentInteractions(seg, svg, {
        category: cat.slug,
        tooltip: { title: d.name, subtitle: coffeesLabel(d.count), products, color: fillColor },
        onActivate: () => toggleDescriptor(svg, d.slug),
      });
      outerSegments.push({ a0, a1, slug: d.slug, name: d.name, count: d.count, color: fillColor });
      descriptorEls.set(d.slug, seg);
      famEls.push(seg);
      svg.appendChild(seg);
      const sweepDeg = a1 - a0;
      if (sweepDeg >= LABEL_SM_ANGLE) {
        const tier = sweepDeg >= LABEL_LG_ANGLE ? 'lg' : 'sm';
        const labelLight = cat.color.light + 8 + ramp * 20 + 4;
        const tone = labelLight > 56 ? 'dark' : 'light';
        const labelEl = radialLabel(mid, d.name, tone, tier);
        descriptorLabelEls.set(d.slug, labelEl);
        svg.appendChild(labelEl);
      }
    });

    const catEnd = angle;
    const catMid = (catStart + catEnd) / 2;
    const catSeg = document.createElementNS(SVG_NS, 'path');
    catSeg.setAttribute('d', arcPath(R_INNER_0, R_INNER_1, catStart, catEnd));
    catSeg.setAttribute('fill', hsl(cat.color, -8, 4));
    catSeg.setAttribute('class', 'coffee-wheel__segment coffee-wheel__segment--category');
    catSeg.setAttribute('role', 'button');
    catSeg.setAttribute('tabindex', '0');
    catSeg.setAttribute('aria-pressed', 'false');
    const catCount = new Set(
      catSlugs.flatMap((s) => [...(productsBySlug.get(s) || [])])
    ).size;
    catSeg.setAttribute('aria-label', `${cat.name} — ${coffeesLabel(catCount)}`);
    catSeg.dataset.wheelCategory = cat.slug;
    catSeg.dataset.wheelCategoryOf = cat.slug;
    catSeg.dataset.wheelDescriptors = catSlugs.join('|');
    const catOrder = segIndex++;
    catSeg.style.setProperty('--seg-i', String(catOrder));
    catSeg.style.setProperty('--seg-glow', hsl(cat.color, 4, 10).replace(')', ' / 0.38)'));
    catSeg.dataset.trill = pentatonicFreq(catOrder).toFixed(1);
    liftVars(catSeg, catMid, 4);
    segmentInteractions(catSeg, svg, {
      category: cat.slug,
      tooltip: { title: cat.name, subtitle: coffeesLabel(catCount), color: hsl(cat.color, -8, 4) },
      onActivate: () => toggleCategory(svg, catSlugs),
    });
    categorySegments.push({
      a0: catStart,
      a1: catEnd,
      slug: cat.slug,
      slugs: catSlugs,
      name: cat.name,
      count: catCount,
    });
    categoryEls.set(cat.slug, catSeg);
    famEls.push(catSeg);
    svg.appendChild(catSeg);
    // the arc label measures rendered text (a forced layout) — deferred to its
    // own build step so an idle slice never pays segments + measurement at once
    labelJobs[catIndex] = () =>
      categoryArcLabel(svg, defs, catIndex, catStart, catEnd, cat.name, '#ffffff');
  };

  // each step self-times into buildCpuMs — the total is a real benchmark of
  // this device that decideLiteMode() reads once the build completes
  const timed = (fn) => () => {
    const t0 = performance.now();
    fn();
    buildCpuMs += performance.now() - t0;
  };
  const steps = [
    ...cats.map((cat, i) => timed(() => buildFamily(cat, i))),
    ...cats.map((cat, i) => timed(() => labelJobs[i] && labelJobs[i]())),
  ];

  const finish = (opts = {}) => {
    // Deferred: the collision sweep forces layout with a getBoundingClientRect
    // per category label. Running it one frame later is invisible (the wheel is
    // still fading in from opacity 0) and shaves that layout burst off the
    // first-open critical path on slow phones. The prebuild path additionally
    // chunks it label-by-label and needs to know when it's done (to unrender).
    requestAnimationFrame(() =>
      resolveCategoryLabelCollisions(svg, opts.chunkedSweep === true, opts.onSweepDone)
    );

    // centre roundel — doubles as a live match counter while selecting
    const centre = document.createElementNS(SVG_NS, 'circle');
    centre.setAttribute('cx', String(CX));
    centre.setAttribute('cy', String(CY));
    centre.setAttribute('r', String(R_INNER_0 - 9));
    centre.setAttribute('class', 'coffee-wheel__centre');
    svg.appendChild(centre);
    const centreLabel = document.createElementNS(SVG_NS, 'text');
    centreLabel.setAttribute('x', String(CX));
    centreLabel.setAttribute('y', String(CY));
    centreLabel.setAttribute('text-anchor', 'middle');
    centreLabel.setAttribute('class', 'coffee-wheel__centre-label');
    centreLabel.dataset.wheelCentreLabel = 'true';
    svg.appendChild(centreLabel);
    updateCentre(svg);

    initWheelDragSelect(svg);

    // The sidebar flavour search lives in the desktop-only right panel (hidden
    // below 1200px). Building its index + DOM is pure waste on phones, so skip it
    // there to shave the first-open cost on weaker devices.
    if (!isMobileViewport()) {
      searchIndex = buildSearchIndex();
      ensureSearchUI(svg);
    }

    // the build just benchmarked this device — pick full or lite experience
    decideLiteMode();
  };

  return { steps, finish };
}

/** Synchronous build — the on-open path, where the user is actively waiting. */
function buildWheel() {
  const build = startWheelBuild();
  build.steps.forEach((step) => step());
  build.finish();
}

function setCentreLines(centreLabel, lines, mode) {
  centreLabel.innerHTML = '';
  centreLabel.setAttribute('data-centre-mode', mode);
  lines.forEach((line, i) => {
    const tspan = document.createElementNS(SVG_NS, 'tspan');
    tspan.setAttribute('x', String(CX));
    if (mode === 'count') {
      // 44px number over 19px noun: fixed baselines centre the block optically
      tspan.setAttribute('dy', i === 0 ? '4' : '30');
      if (i === 0) tspan.setAttribute('class', 'coffee-wheel__centre-count');
    } else {
      tspan.setAttribute('dy', i === 0 ? `${-(lines.length - 1) * 0.35}em` : '1.24em');
      if (mode === 'hover' && i === lines.length - 1) tspan.setAttribute('class', 'coffee-wheel__centre-sub');
    }
    tspan.textContent = line;
    centreLabel.appendChild(tspan);
  });
}

function updateCentre(svg) {
  const centreLabel = svg.querySelector('[data-wheel-centre-label]');
  if (!centreLabel) return;
  const modal = getModal();
  const count = matchingProductCount();
  if (count === null) {
    setCentreLines(centreLabel, (modal.dataset.labelCentre || 'Find your\nflavour').split('\n'), 'idle');
  } else {
    const noun = (count === 1 ? modal.dataset.labelCoffee : modal.dataset.labelCoffees) || '{count} coffees';
    setCentreLines(centreLabel, [String(count), noun.replace('{count}', '').trim()], 'count');
    centreLabel.classList.remove('coffee-wheel__centre-label--pop');
    void centreLabel.getBBox();
    centreLabel.classList.add('coffee-wheel__centre-label--pop');
  }
}

/** While hovering a segment the centre echoes the flavour under the pointer. */
function updateCentreHover(svg, name, subtitle) {
  const centreLabel = svg.querySelector('[data-wheel-centre-label]');
  if (!centreLabel) return;
  const words = String(name).split(/\s+/);
  const lines =
    words.length > 1 && name.length > 12
      ? [words.slice(0, Math.ceil(words.length / 2)).join(' '), words.slice(Math.ceil(words.length / 2)).join(' '), subtitle]
      : [name, subtitle];
  setCentreLines(centreLabel, lines, 'hover');
}

function rectsIntersect(a, b) {
  return (
    a.width > 0 &&
    b.width > 0 &&
    a.x < b.x + b.width &&
    b.x < a.x + a.width &&
    a.y < b.y + b.height &&
    b.y < a.y + a.height
  );
}

/**
 * De-collide category labels. Radial labels may slide along their spoke, but
 * only within the inner band (based on their actual rendered length) so they
 * can never poke past the ring edges. Shrink next, drop as a last resort.
 *
 * `chunked` spreads the per-label work (a deliberate write→measure layout
 * loop) across idle slices — as one task it measured ~1.9s on a 6×-throttled
 * CPU during the idle prebuild. `onDone` fires after the last label either way.
 */
function resolveCategoryLabelCollisions(svg, chunked, onDone) {
  const labels = [...svg.querySelectorAll('.coffee-wheel__category-label')];

  // getBoundingClientRect (not getBBox) so each label's rotation is respected:
  // a radial label is a near-vertical ribbon, but its un-rotated getBBox reads
  // as a wide horizontal box that phantom-collides with the neighbouring spoke
  // and needlessly shunts it off-centre (e.g. Herbal beside Winey).
  const collidesEarlier = (index) => {
    const box = labels[index].getBoundingClientRect();
    for (let k = 0; k < index; k++) {
      if (labels[k].isConnected && rectsIntersect(labels[k].getBoundingClientRect(), box)) return true;
    }
    return false;
  };

  const tryRadialPlacements = (el) => {
    const midDeg = parseFloat(el.dataset.midDeg);
    const baseR = parseFloat(el.dataset.baseR);
    const half = el.getComputedTextLength() / 2;
    const min = R_INNER_0 + half + 3;
    const max = R_INNER_1 - half - 3;
    if (min > max) return false; // label longer than the band allows
    const candidates = [
      Math.min(Math.max(baseR, min), max),
      max,
      min,
      (min + max) / 2,
    ];
    const index = labels.indexOf(el);
    for (const r of candidates) {
      positionRadialLabel(el, r, midDeg);
      if (!collidesEarlier(index)) return true;
    }
    return false;
  };

  const settleLabel = (j) => {
    const later = labels[j];
    if (!later.isConnected || !collidesEarlier(j)) return;
    if (later.dataset.midDeg && tryRadialPlacements(later)) return;
    later.classList.add('coffee-wheel__category-label--tight');
    if (later.dataset.midDeg && tryRadialPlacements(later)) return;
    if (!collidesEarlier(j)) return;
    // last resort: shrink once more and KEEP it — removing reads as "labels
    // missing" (Mint/Stone vanished on phones); a small label beats none, and
    // the drag zoom magnifies it back to legible anyway
    later.classList.add('coffee-wheel__category-label--micro');
    if (later.dataset.midDeg) tryRadialPlacements(later);
  };

  if (!chunked) {
    for (let j = 1; j < labels.length; j++) settleLabel(j);
    if (onDone) onDone();
    return;
  }

  let j = 1;
  const run = () => {
    if (j >= labels.length) {
      if (onDone) onDone();
      return;
    }
    settleLabel(j);
    j += 1;
    idleSlice(run);
  };
  run();
}

function syncSelectionFromUrl(svg) {
  selected.clear();
  const params = new URLSearchParams(window.location.search);
  (params.get('descriptor') || '')
    .split(',')
    .flatMap((token) => token.split('~'))
    .filter(Boolean)
    .forEach((slug) => selected.add(slug));
  refreshSelectionStyles(svg);
}

/**
 * Memoised fetch of the taxonomy JSON. Prewarmed at idle after page load so the
 * network round-trip is already done (or in flight) by the time the user opens
 * the wheel — the first open then only pays the build cost, not the fetch.
 */
let taxonomyPromise = null;
function loadTaxonomy() {
  if (!taxonomyPromise) {
    const modal = getModal();
    const src = modal && modal.dataset.wheelSrc;
    if (!src) return Promise.reject(new Error('no wheel source'));
    taxonomyPromise = fetch(src).then((r) => r.json());
  }
  return taxonomyPromise;
}

/**
 * Build the wheel ahead of time while the browser is idle, so the first tap on
 * "Explore flavours" opens instantly instead of paying the build cost behind a
 * blank stage (~300ms desktop, ~2.5s-class on old phones). SVG text can only
 * be measured while rendered, so the closed dialog is force-rendered
 * invisibly for the build — display:block + visibility:hidden keeps layout
 * and text metrics real with no top layer, no focus steal, nothing painted.
 *
 * The build runs one family per idle slice (not one big task): a monolithic
 * prebuild measured ~2.5s on a 6×-throttled CPU, which would freeze the page
 * mid-scroll if the idle timeout forced it at a bad moment. If the user opens
 * the modal mid-build, openModal drains the remaining families synchronously.
 */
let pendingBuild = null;

function idleSlice(fn) {
  if ('requestIdleCallback' in window) window.requestIdleCallback(fn);
  else window.setTimeout(() => fn(null), 60); // Safari: one family per tick
}

function prebuildWheel() {
  const modal = getModal();
  if (!modal || built || pendingBuild || modal.open) return;
  loadTaxonomy()
    .then((data) => {
      if (built || pendingBuild || modal.open) return;
      taxonomy = data;
      modal.style.display = 'block';
      modal.style.visibility = 'hidden';
      const build = startWheelBuild();
      let next = 0;
      pendingBuild = {
        // openModal calls this when the user taps mid-build: they are now
        // waiting, so finish the remaining steps synchronously. openModal
        // clears the force-render styles and shows the dialog itself, and the
        // collision sweep runs as the usual single rAF against the open modal.
        drain() {
          while (next < build.steps.length) build.steps[next++]();
          build.finish();
          built = true;
          pendingBuild = null;
        },
      };
      const chunk = (deadline) => {
        if (!pendingBuild) return; // drained by an open
        // always at least one step per slice so progress is guaranteed
        do {
          build.steps[next++]();
        } while (next < build.steps.length && deadline && deadline.timeRemaining() > 6);
        if (next < build.steps.length) {
          idleSlice(chunk);
          return;
        }
        pendingBuild = null;
        built = true; // fully usable now — the label sweep below is cosmetic
        // the label-collision sweep also runs in idle slices; only once it has
        // settled every label is the hidden render safe to drop — unless the
        // user opened the modal mid-sweep, in which case it must stay rendered
        build.finish({
          chunkedSweep: true,
          onSweepDone: () => {
            if (!modal.open) {
              modal.style.display = '';
              modal.style.visibility = '';
            }
          },
        });
      };
      idleSlice(chunk);
    })
    .catch(() => {
      // taxonomy asset unavailable; the normal open path will retry
    });
}

function openModal() {
  const modal = getModal();
  if (!modal) return;
  // The idle prebuild may be mid-flight: finish the remaining steps now — the
  // user is waiting. Drain BEFORE clearing the invisible force-render: label
  // steps measure rendered text, and the moment display is cleared the closed
  // dialog is display:none, where getComputedTextLength() reads 0 and every
  // label "fits" — shipping overflowing, colliding arc labels.
  if (pendingBuild) pendingBuild.drain();
  modal.style.display = '';
  modal.style.visibility = '';
  // launched from inside the filter drawer — close it so the wheel stands alone
  const drawer = document.getElementById('coffee-filters-drawer');
  if (drawer && drawer.isOpen === true && typeof drawer.close === 'function') {
    drawer.close();
  }
  // the dialog must be open before building: SVG text can only be measured
  // (label fitting, collision sweep) while it is rendered
  modal.showModal();
  applyTune();
  userEngaged = false; // fresh open — let the tutorial coach show again
  // keep the roast toggle in step with the sidebar's browse filter: opening
  // with ?browse=espresso|filter pre-selects that mode, anything else = All
  const browse = (new URLSearchParams(window.location.search).get('browse') || '').split(',')[0];
  roastMode = browse === 'espresso' || browse === 'filter' ? browse : 'all';
  const svg = modal.querySelector('[data-coffee-wheel-svg]');
  // Mobile replays the intro/coach every open; desktop keeps its wave-once.
  const runIntro = () => {
    if (isMobileViewport()) {
      playIntro(svg);
    } else if (!hasAnimatedIn) {
      hasAnimatedIn = true;
      playIntro(svg);
    } else {
      svg.classList.add('coffee-wheel--live');
    }
  };
  if (!built) {
    loadTaxonomy()
      .then((data) => {
        taxonomy = data;
        built = true;
        buildWheel();
        syncSelectionFromUrl(svg);
        runIntro();
      })
      .catch(() => {
        // taxonomy asset unavailable; leave the modal empty but functional
      });
  } else {
    // the wheel is static for the page's lifetime — just re-sync selections
    syncSelectionFromUrl(svg);
    runIntro();
  }
}

/**
 * First-open intro: slices grow from the core in a clockwise wave to 110%,
 * trilling as they bloom, then the whole ring settles to 100% with a doosh.
 */
function playIntro(svg) {
  // Mobile & reduced-motion: skip the 87-segment wave (expensive on weak
  // phones). A quick, replayable fade-in + the coach teach the drag gesture and
  // run every open. The richer bloom stays for desktop only.
  if (isCoarsePointer() || prefersReducedMotion()) {
    // reset then re-add so the opacity fade replays on every open (reduced
    // motion keeps opacity:1 via CSS, so it simply shows without a fade)
    svg.classList.remove('coffee-wheel--live', 'coffee-wheel--wave', 'coffee-wheel--settle');
    void svg.getBoundingClientRect();
    svg.classList.add('coffee-wheel--live');
    window.setTimeout(maybeShowCoach, prefersReducedMotion() ? 250 : 480);
    return;
  }

  svg.classList.add('coffee-wheel--live');
  svg.classList.remove('coffee-wheel--settle');
  svg.classList.add('coffee-wheel--wave');
  const segs = svg.querySelectorAll('.coffee-wheel__segment');

  segs.forEach((seg, idx) => {
    if (idx % 3 !== 0) return; // a light ripple, not 87 notes
    window.setTimeout(() => {
      trill(Number(seg.dataset.trill) || 700, 0.017, 0.07);
    }, idx * TUNE.waveStep);
  });

  const waveTotal = segs.length * TUNE.waveStep + TUNE.waveDur;
  window.setTimeout(() => {
    svg.classList.remove('coffee-wheel--wave');
    svg.classList.add('coffee-wheel--settle');
    doosh();
    window.setTimeout(() => {
      svg.classList.remove('coffee-wheel--settle');
      maybeShowCoach();
    }, TUNE.settleDur + 80);
  }, waveTotal);
}

function applySelection() {
  const modal = getModal();
  // one token per family: slugs joined by '~' are OR-ed, tokens are AND-ed
  let tokens = [...selectedByFamily().values()].map((slugs) => slugs.join('~'));
  if (selected.size && matchingProductCount() === 0) {
    // "Show closest matches": nothing carries every family, so relax to a
    // single OR across everything selected — the grid then shows the union
    // that the closest-matches list ranks, instead of filtering to zero
    tokens = [[...selected].join('~')];
  }
  document.dispatchEvent(
    new CustomEvent('coffee:wheel-apply', {
      // roastMode rides along so the sidebar espresso/filter browse filter
      // stays in step with the wheel's toggle
      detail: { descriptors: tokens, browse: roastMode },
    })
  );
  modal.close();
}

function boot() {
  const modal = getModal();
  if (!modal || modal.dataset.wheelBound === 'true') return;
  modal.dataset.wheelBound = 'true';
  const params = new URLSearchParams(window.location.search);

  document.querySelectorAll('[data-coffee-wheel-open]').forEach((btn) => {
    btn.addEventListener('click', openModal);
  });

  // Deep link: ?wheel=1 opens the wheel on arrival (used by the coffee-lingo
  // education page's "explore the flavour wheel" links). Deferred a beat so
  // the collection paints first and the dialog opens over a settled page.
  if (params.get('wheel') === '1') {
    window.setTimeout(openModal, 400);
  }

  // Prebuild the whole wheel (taxonomy fetch + SVG) while the browser is idle
  // so the first open is instant. Harmless if the wheel never opens.
  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(prebuildWheel, { timeout: 4000 });
  } else {
    window.setTimeout(prebuildWheel, 1500);
  }
  modal.querySelector('[data-coffee-wheel-apply]')?.addEventListener('click', applySelection);

  // roast-type toggle — every copy (footer + panel) shares the same state
  modal.querySelectorAll('[data-coffee-wheel-roast] [data-roast-option]').forEach((btn) => {
    btn.addEventListener('click', () => {
      const mode = btn.dataset.roastOption;
      if (mode !== 'all' && mode !== 'espresso' && mode !== 'filter') return;
      if (roastMode === mode) return;
      roastMode = mode;
      refreshSelectionStyles(modal.querySelector('[data-coffee-wheel-svg]'));
    });
  });

  // one handler for every Clear control (footer on mobile, sidebar on desktop)
  const clearSelection = () => {
    selected.clear();
    roastMode = 'all'; // full reset — roast narrowing clears with the flavours
    // reset the search field too so a cleared wheel starts fully fresh
    const searchInput = modal.querySelector('[data-coffee-wheel-search-input]');
    if (searchInput) searchInput.value = '';
    const searchList = modal.querySelector('[data-coffee-wheel-search-results]');
    if (searchList) {
      searchList.hidden = true;
      searchList.innerHTML = '';
    }
    refreshSelectionStyles(modal.querySelector('[data-coffee-wheel-svg]'));
  };
  modal.querySelectorAll('[data-coffee-wheel-clear]').forEach((btn) => {
    btn.addEventListener('click', clearSelection);
  });
  modal.querySelector('[data-coffee-wheel-close]')?.addEventListener('click', () => modal.close());
  modal.addEventListener('click', (event) => {
    if (event.target === modal) modal.close();
  });

  // Reset the tutorial state on close so it replays fresh next open (mobile).
  modal.addEventListener('close', () => {
    window.clearTimeout(coachTimer);
    const coach = modal.querySelector('[data-coffee-wheel-coach]');
    if (coach) coach.hidden = true;
    userEngaged = false;
    if (isMobileViewport()) {
      const svg = modal.querySelector('[data-coffee-wheel-svg]');
      // drop to pre-intro state so the fade + coach animate again next open
      if (svg) svg.classList.remove('coffee-wheel--live', 'coffee-wheel--wave', 'coffee-wheel--settle');
    }
  });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

document.addEventListener('shopify:section:load', boot);
